import { register } from './index';
import { getInstanceDefinition, getDefinition } from '../cardLookup';
import { useDungeonAbility } from '../../actions/abilityActions';
import {
  destroyFromHand, discardFromHand, gainCurse, grantXp, drawCards, revealTopOfOwnDeck,
  hasTraitPresent, applyInstanceOverride, grantPhysicalAttack, grantMagicAttack,
  battleTargetableHeroes, excludeShieldedHeroes, isHeroLike, queueHandChoice, queueOptionalDiscard,
  applyDoWhatYouCan, destroyCurseFromHand, moveHallCard, queueEffectChoice, queueAttackValueChoice,
  pickRandomInstanceIds, pickRandomInstanceId, queueDiscardPileChoice, shiftHallCardToRank,
  estimateHeroAttackValue, grantTemporaryStrength,
} from '../effectHelpers';
import { performLevelUp } from '../../village/leveling';
import { buyOneCard } from '../buyOneCard';
import { computeTotalLight, hasDarknessAtRank } from '../../combat/lightDarkness';
import { HALL_RANK_COUNT } from '../../constants';

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

// Into the Abyss, Heart of Doom: an eliminated player (cards/effectHelpers.js's
// eliminatePlayer) is excluded here so every "each player"/"each other
// player" monster effect built on top of this shared helper (Charred
// Bruin, Blazing Grimalkin, Pyre Viper, Hellhound, Shrouded Cadaver)
// automatically skips them too, without touching each one individually.
function allPlayerIds(state) {
  return state.players.filter((p) => !p.eliminated).map((p) => p.id);
}

function otherPlayerIds(state, playerId) {
  return allPlayerIds(state).filter((id) => id !== playerId);
}

function anyAmbusherInHall(state) {
  return state.dungeonHall.some((id) => id && getInstanceDefinition(state, id).traits.includes('Ambusher'));
}

// Shared "something happens when a player visits the village, depending on
// what's currently in the hall" hook - the first two cards needing it are
// Desmodic (CoB) and ToR's own Magma Wyrm, both previously flagged as
// missing this exact infrastructure. Not a generic dispatcher for every
// monster's `global` ability text (Global effects trigger on many different
// things across this card pool - Belac/Stramst fire on combat, Caldera
// Dragon on any card draw - so there's no single "fire every hall monster's
// Global timing" event to hook into); this is its own narrow, purpose-built
// lookup table for specifically the "on village visit" trigger, called once
// from actions/index.js's VISIT_VILLAGE case, the same way Trophy effects
// are resolved automatically there.
const VILLAGE_VISIT_HALL_EFFECTS = {
  // "After a player visits the village, this card switches ranks with the
  // monster in front of it." "In front of" = the next-lower rank (closer to
  // rank 1); a no-op at rank 1 itself (nothing in front of it).
  'monster-magma-wyrm': (state, { hallRankIndex }) => {
    if (hallRankIndex === 0) return state;
    return moveHallCard(state, hallRankIndex, hallRankIndex - 1);
  },
  // "Global: If there is an Ambusher in the hall, each time a player visits
  // the village, that player destroys the top card of 1 hero stack." No
  // selection method specified on the card - defaults to the first
  // non-empty hero stack (Object.keys() insertion order), a documented
  // judgment call rather than a genuine player choice, matching this
  // engine's convention for other automatic (non-interactive) triggers like
  // Trophy effects. True "destroy" (Magma Wyrm's own Breach - "Destroy the
  // top 2 cards of each village and hero stack" - is the existing precedent
  // for removing straight off a heroStacks array with no return-elsewhere
  // step), not a level-up or any other special handling.
  'monster-desmodic': (state, { playerId }) => {
    if (!anyAmbusherInHall(state)) return state;
    const targetKey = Object.keys(state.village.heroStacks).find((key) => state.village.heroStacks[key].length);
    if (!targetKey) return state;
    return {
      ...state,
      players: state.players.map((p) => (p.id === playerId ? { ...p, stats: { ...p.stats, cardsDestroyed: p.stats.cardsDestroyed + 1 } } : p)),
      village: { ...state.village, heroStacks: { ...state.village.heroStacks, [targetKey]: state.village.heroStacks[targetKey].slice(1) } },
    };
  },
};

// Runs every entry in VILLAGE_VISIT_HALL_EFFECTS whose monster is currently
// in the hall. Scans off the ORIGINAL (pre-dispatch) hall order/contents so
// one handler repositioning the hall mid-pass (Magma Wyrm) can't cause a
// second handler to be skipped or double-fired - each handler's own current
// rank is re-looked-up right before it runs, off the state as of that point
// in the pass.
export function resolveVillageVisitHallEffects(state, playerId) {
  let nextState = state;
  state.dungeonHall.forEach((instanceId) => {
    if (!instanceId) return;
    const def = getInstanceDefinition(nextState, instanceId);
    const handler = VILLAGE_VISIT_HALL_EFFECTS[def.id];
    if (!handler) return;
    const hallRankIndex = nextState.dungeonHall.indexOf(instanceId);
    if (hallRankIndex === -1) return; // no longer in the hall at all
    nextState = handler(nextState, { playerId, hallRankIndex, monsterInstanceId: instanceId });
  });
  return nextState;
}

// Same "something happens when a player visits, depending on what's
// currently in the hall" shape as VILLAGE_VISIT_HALL_EFFECTS above, but for
// ENTER_DUNGEON instead of VISIT_VILLAGE - the Thunderstone Bearer is
// shuffled into the bottom of the dungeon deck at setup (see
// dungeon/dungeonDeckSetup.js) and drawn into the hall like any other
// monster once the deck depletes enough, so a plain hall-presence check
// works identically to every other hall-presence gate in this file.
const DUNGEON_VISIT_HALL_EFFECTS = {
  // Into the Abyss, Heart of Doom: "Global: When a player enters the
  // dungeon, destroy all monsters present." "Present" = monster-type cards
  // in the ENTERING player's own hand (same convention as Veteran
  // Trainer's Village ability/Legion General's fight-eligibility check) -
  // monster-type cards essentially never sit in hand under normal play
  // (see effectHelpers.js's isHeroLike comment on the same recurring
  // caveat), so this rarely actually destroys anything.
  'bearer-heart-of-doom': (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const monsterIds = player.hand.filter((id) => getInstanceDefinition(state, id).type === 'monster');
    let nextState = state;
    for (const id of monsterIds) nextState = destroyFromHand(nextState, playerId, id);
    return nextState;
  },
  // Into the Abyss, Oblivion, Deathkeeper: "Global: Discard 1 random card
  // when you enter the dungeon." "You" = the entering player.
  'bearer-oblivion-deathkeeper': (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const { state: afterPick, instanceId } = pickRandomInstanceId(state, player.hand);
    return instanceId ? discardFromHand(afterPick, playerId, instanceId) : afterPick;
  },
};

export function resolveDungeonVisitHallEffects(state, playerId) {
  let nextState = state;
  state.dungeonHall.forEach((instanceId) => {
    if (!instanceId) return;
    const def = getInstanceDefinition(nextState, instanceId);
    const handler = DUNGEON_VISIT_HALL_EFFECTS[def.id];
    if (!handler) return;
    if (nextState.dungeonHall.indexOf(instanceId) === -1) return; // no longer in the hall at all
    nextState = handler(nextState, { playerId });
  });
  return nextState;
}

// Numenera, Bandit Taskmaster/Bandit Warrior: "Global: You must use all
// Dungeon abilities (if possible)." An obligation lock, not a discrete
// effect - checked live at the two points a Dungeon turn can move past the
// ability-usage window (actions/index.js's CHOOSE_MONSTER/END_DUNGEON_TURN
// cases, mirroring the existing "must fight a monster" check already there)
// rather than a one-shot handler. Only the entry's own base legality
// matters here (foughtMonsterThisTurn already gates every Dungeon ability
// universally, so once that's true there's nothing left to be obligated to
// use).
//
// "If possible" is checked by actually DRY-RUNNING useDungeonAbility (every
// handler is a pure (state, ctx) -> newState function, so discarding the
// result is entirely safe) rather than mirroring its preconditions via
// hasHandler/isUsable/getCandidates - those hooks only cover a card's OWN
// stated precondition, not every OTHER rule useHandCardAbility enforces
// (e.g. hero-clever's "This hero can only use one ability" group-wide cap,
// caught the hard way: an earlier isUsable/getCandidates-based version
// declared Clever Nano's second Dungeon line "usable" even after its first
// had already been used, which useDungeonAbility itself then correctly
// rejected - AI self-play testing surfaced it). Dry-running sidesteps the
// whole class of "some OTHER precondition I didn't know to check for."
//
// "Already used" is checked against turnFlags.triggeredAbilityInstanceIds,
// NOT usedAbilityInstanceIds - the latter is deliberately left unpopulated
// for repeatable abilities (Root of Corruption, Regian: "Destroy 1 disease
// to draw 1 card," repeatable AND a silent no-op with no disease present -
// dry-running it would never throw, so it would otherwise look "usable"
// forever), but "you must use it" only obligates ONE trigger, not
// exhausting every repeat.
const BANDIT_OBLIGATION_MONSTER_IDS = new Set(['monster-bandit-taskmaster', 'monster-bandit-warrior']);
function anyBanditObligationMonsterInHall(state) {
  return state.dungeonHall.some((id) => id && BANDIT_OBLIGATION_MONSTER_IDS.has(getInstanceDefinition(state, id).id));
}
function canActuallyUseDungeonAbility(state, playerId, instanceId, abilityIndex) {
  try {
    useDungeonAbility(state, { playerId, instanceId, abilityIndex, choices: {} });
    return true;
  } catch {
    return false;
  }
}
export function hasUnmetBanditObligation(state, playerId) {
  if (!anyBanditObligationMonsterInHall(state)) return false;
  const player = state.players.find((p) => p.id === playerId);
  if (player.turnFlags.foughtMonsterThisTurn) return false;
  return player.hand.some((instanceId) => {
    const def = getInstanceDefinition(state, instanceId);
    return (def.abilityText.dungeon || []).some((entry, abilityIndex) => {
      const key = `${instanceId}:dungeon:${abilityIndex}`;
      if (player.turnFlags.triggeredAbilityInstanceIds.includes(key)) return false;
      return canActuallyUseDungeonAbility(state, playerId, instanceId, abilityIndex);
    });
  });
}

// --- Burnmarked -------------------------------------------------------------

// "Each player destroys 1 card" - every player, including the active one,
// picks their own (Matt: "I should be able to choose... across the card
// base" - this used to auto-pick player.hand[0] for everyone). Queues one
// entry per player with a non-empty hand; each resolves it on their own
// (PlayerDock.vue's viewingPlayer switches to show whoever's entry is next,
// even mid another player's turn - see its gameState watcher).
register('monster-charred-bruin', {
  battle: [{
    resolve: (state) => {
      let nextState = state;
      for (const id of allPlayerIds(nextState)) {
        const player = nextState.players.find((p) => p.id === id);
        nextState = queueHandChoice(nextState, {
          playerId: id, verb: 'destroy', candidateInstanceIds: [...player.hand], reason: 'Charred Bruin: each player destroys 1 card.',
        });
      }
      return nextState;
    },
  }],
});

// "Each player may choose to discard 3 cards. If a player chooses not to,
// that player loses 1 XP." Each affected player (not just the active one -
// same as Charred Bruin above) gets their own opt-in/decline gate, queued
// via queueOptionalDiscard rather than the old choices?.discardingPlayerIds
// (dead code, same as every other "choices" fallback this Battle/Aftermath
// handler shape used to have - see resolveCombat.js's resolveTimedHandlers).
register('monster-blazing-grimalkin', {
  battle: [{
    resolve: (state) => {
      let nextState = state;
      for (const id of allPlayerIds(nextState)) {
        nextState = queueOptionalDiscard(nextState, {
          playerId: id, count: 3, declineXpPenalty: 1, reason: 'Blazing Grimalkin: discard 3 cards, or lose 1 XP.',
        });
      }
      return nextState;
    },
  }],
});

register('monster-pyre-viper', {
  // "Each player must either show a curse or gain 2 curses."
  battle: [{
    resolve: (state) => {
      let nextState = state;
      for (const id of allPlayerIds(nextState)) {
        const hasCurse = hasTraitPresent(nextState, id, 'Curse');
        if (!hasCurse) {
          nextState = gainCurse(nextState, id);
          nextState = gainCurse(nextState, id);
        }
      }
      return nextState;
    },
  }],
});

register('monster-phoenix', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
  trophy: [{
    resolve: (state, { playerId, instanceId }) => {
      const withXp = grantXp(state, playerId, 3);
      // Returns to the top of the dungeon deck every time it's Trophy'd -
      // removed from wherever it currently sits (hand/discard) first.
      let nextState = updatePlayer(withXp, playerId, (p) => ({
        ...p,
        hand: p.hand.filter((id) => id !== instanceId),
        discard: p.discard.filter((id) => id !== instanceId),
      }));
      nextState = { ...nextState, dungeonDeck: [instanceId, ...nextState.dungeonDeck] };
      return nextState;
    },
  }],
});

// "Each other player must discard 1 hero, or show a hand with none" - each
// affected opponent picks which of their own heroes (Matt: same "let me
// choose" issue as Charred Bruin - this used to auto-pick
// battleTargetableHeroes(...)[0] for them). "Or show a hand with none" is
// already handled for free: queueHandChoice no-ops when candidates is empty.
register('monster-hellhound', {
  battle: [{
    resolve: (state, { playerId }) => {
      let nextState = gainCurse(state, playerId);
      for (const id of otherPlayerIds(nextState, playerId)) {
        const player = nextState.players.find((p) => p.id === id);
        nextState = queueHandChoice(nextState, {
          playerId: id, verb: 'discard', candidateInstanceIds: battleTargetableHeroes(nextState, player), reason: 'Hellhound: discard 1 hero, or show a hand with none.',
        });
      }
      return nextState;
    },
  }],
  aftermath: [{ resolve: (state, { playerId, won }) => (won ? state : gainCurse(state, playerId)) }],
});

// --- Corvaxis -----------------------------------------------------------

register('monster-blackflock-conniver', {
  battle: [{
    resolve: (state, { playerId }) => {
      if (!anyAmbusherInHall(state)) return state;
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      for (const weaponId of Object.values(player.equippedWeapons).flat()) nextState = discardFromHand(nextState, playerId, weaponId);
      return updatePlayer(nextState, playerId, (p) => ({ ...p, equippedWeapons: {} }));
    },
  }],
});

// Reveals the top card of the active player's own deck (rulebook FAQ:
// revealed cards are never added to hand, and go to discard unless a
// specific effect destroys them instead - the caller decides which). Adds
// its gold value to the fought monster's Health, then lets `destroyIf`
// decide whether to destroy it outright (skipping the discard) or leave it
// discarded. Returns the updated state.
function revealTopAddGoldToHealth(state, { playerId, monsterInstanceId }, destroyIf) {
  const player = state.players.find((p) => p.id === playerId);
  if (!player.deck.length) return state;
  const [revealedId, ...restDeck] = player.deck;
  const revealedDef = getInstanceDefinition(state, revealedId);
  const monsterDef = getInstanceDefinition(state, monsterInstanceId);
  let nextState = applyInstanceOverride(state, monsterInstanceId, {
    health: (monsterDef.health || 0) + (revealedDef.goldValue || 0),
  });
  const shouldDestroy = destroyIf(revealedDef);
  nextState = updatePlayer(nextState, playerId, (p) => ({
    ...p,
    deck: restDeck,
    discard: shouldDestroy ? p.discard : [...p.discard, revealedId],
  }));
  return nextState;
}

register('monster-slaughterwing', {
  battle: [{
    resolve: (state, ctx) => revealTopAddGoldToHealth(state, ctx, (def) => isHeroLike(def) && def.level <= 2),
  }],
});

register('monster-plunderwing', {
  battle: [{
    resolve: (state, ctx) => revealTopAddGoldToHealth(
      state, ctx, (def) => def.traits.includes('Weapon') || def.traits.includes('Item'),
    ),
  }],
});

register('monster-blackflock-ravener', {
  raid: [{
    resolve: (state, { playerId }) => {
      if (!playerId) return state; // no active-player context (e.g. initial setup) - skip
      const player = state.players.find((p) => p.id === playerId);
      return queueHandChoice(state, {
        playerId, verb: 'destroy', candidateInstanceIds: [...player.hand], reason: 'Blackflock Ravener raids: destroy 1 card in your hand.',
      });
    },
  }],
});

register('monster-blackflock-assassin', {
  raid: [{
    resolve: (state, { playerId }) => {
      if (!playerId || !anyAmbusherInHall(state)) return state;
      const player = state.players.find((p) => p.id === playerId);
      const heroIds = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      return queueHandChoice(state, {
        playerId, verb: 'destroy', candidateInstanceIds: heroIds, reason: 'Blackflock Assassin raids: destroy 1 hero in your hand.',
      });
    },
  }],
});

// --- Djinni: "Gain 1 curse if you have no <class>" -------------------------

function curseIfMissingTrait(trait) {
  return (state, { playerId }) => (hasTraitPresent(state, playerId, trait) ? state : gainCurse(state, playerId));
}

register('monster-ghul', { battle: [{ resolve: curseIfMissingTrait('Fighter') }] });
register('monster-ifrit', { battle: [{ resolve: curseIfMissingTrait('Ranger') }] });
register('monster-majnun', { battle: [{ resolve: curseIfMissingTrait('Wizard') }] });

register('monster-iblis', {
  battle: [{
    resolve: (state, { playerId }) => {
      let nextState = curseIfMissingTrait('Cleric')(state, { playerId });
      // "Cancel all Light that is not provided by heroes" - approximated as
      // forcing Light to 0 for the rest of this combat unless the player's
      // hand is light-sourced entirely by hero cards (rare in ToR's basic
      // Light sources, which are Items/Weapons, not Heroes).
      const player = nextState.players.find((p) => p.id === playerId);
      const nonHeroLight = player.hand.some((id) => {
        const def = getInstanceDefinition(nextState, id);
        return def.type !== 'hero' && (def.light || 0) > 0;
      });
      if (nonHeroLight) {
        nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, turnFlags: { ...p.turnFlags, lightForcedToZero: true } }));
      }
      return nextState;
    },
  }],
});

register('monster-marid', {
  battle: [{
    resolve: (state, { playerId }) => {
      let nextState = curseIfMissingTrait('Thief')(state, { playerId });
      // "Cancel all Spoils abilities" this combat.
      return updatePlayer(nextState, playerId, (p) => ({ ...p, turnFlags: { ...p.turnFlags, spoilsCancelled: true } }));
    },
  }],
});

// --- Dragon -----------------------------------------------------------------

// NOTE: the `choices?.instanceId` this used to check was dead code -
// resolveCombat.js's resolveTimedHandlers hardcodes `choices: {}` for every
// Battle/Aftermath handler call, so a value supplied on CHOOSE_MONSTER's
// payload.choices could never actually reach here. Converted to the same
// pending-queue pattern as Raid instead (see queueHandChoice).
register('monster-cinder-drake', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Cinder Drake: destroy 1 weapon.' });
    },
  }],
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Spell'));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Cinder Drake: destroy 1 spell.' });
    },
  }],
});

register('monster-caldera-dragon', {
  // Global "after drawing 1+ cards via an ability, discard 1" - no registered
  // handler here on purpose. Wired directly into actions/abilityActions.js's
  // useHandCardAbility (see applyCalderaDragonDiscard there), the one choke
  // point every village/dungeon/spoils ability already passes through, rather
  // than a generic draw hook every draw-granting handler would need to know
  // about.
});

register('monster-smokeplume-drake', {
  // "Discard 2 cards" (no filter) - no "auto-pick the first 2" here either;
  // queues 2 single-pick entries off the same candidate list (the pre-effect
  // hand) rather than inventing a separate multi-select UI. resolveHandChoice
  // already re-checks hand-membership at resolution time, so the second
  // entry naturally rejects re-picking whatever the first one just removed -
  // "do what you can" (applyDoWhatYouCan) still applies via Math.min if the
  // hand has fewer than 2 cards.
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const count = Math.min(2, player.hand.length);
      let nextState = state;
      for (let i = 0; i < count; i += 1) {
        nextState = queueHandChoice(nextState, {
          playerId, verb: 'discard', candidateInstanceIds: [...player.hand], reason: 'Smokeplume Drake: discard 2 cards.',
        });
      }
      return nextState;
    },
  }],
  // "Destroy 1 hero with the highest strength" - unlike "destroy 1 hero
  // (matching filter)", the card text itself picks a specific hero (the
  // strongest), not a free choice among several - same deterministic-by-
  // design shape as Belac's "highest Attack Value". Left as an algorithmic
  // pick, not a queued choice.
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroes = player.hand
        .map((id) => ({ id, def: getInstanceDefinition(state, id) }))
        .filter((c) => isHeroLike(c.def))
        .sort((a, b) => (b.def.strength || 0) - (a.def.strength || 0));
      return heroes.length ? destroyFromHand(state, playerId, heroes[0].id, { battleEffect: true }) : state;
    },
  }],
});

register('monster-ash-dragon', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      const targets = battleTargetableHeroes(state, player, (def) => (def.level || 0) <= 1);
      for (const id of targets) nextState = discardFromHand(nextState, playerId, id);
      return nextState;
    },
  }],
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      const targets = player.hand.filter((id) => (getInstanceDefinition(state, id).light || 0) > 0);
      for (const id of targets) nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
      return nextState;
    },
  }],
});

register('monster-magma-wyrm', {
  breach: [{
    resolve: (state) => {
      let nextState = state;
      for (const key of Object.keys(nextState.village.heroStacks)) {
        nextState = { ...nextState, village: { ...nextState.village, heroStacks: { ...nextState.village.heroStacks, [key]: nextState.village.heroStacks[key].slice(2) } } };
      }
      for (const key of Object.keys(nextState.village.villageStacks)) {
        nextState = { ...nextState, village: { ...nextState.village, villageStacks: { ...nextState.village.villageStacks, [key]: nextState.village.villageStacks[key].slice(2) } } };
      }
      return nextState;
    },
  }],
});

// --- Kobold -----------------------------------------------------------------

register('monster-drakeclan-laird', {
  battle: [{
    // Always computed from *printed* (base) Health, for every card involved
    // - never from getInstanceDefinition's possibly-already-boosted value.
    // An earlier version read the current (potentially overridden) Health as
    // its base, which compounds without bound: fighting this card (even on a
    // loss - Battle effects fire regardless of outcome) permanently raised
    // its Health using the *other* Drakeclan Laird's already-boosted Health,
    // and vice versa, so two copies in the hall together spiraled into an
    // unbeatable monster within a few dozen fights. Caught via AI self-play
    // games that stalled for tens of thousands of turns with Health values
    // like 31 and 50 on a card whose printed Health is 7.
    resolve: (state, { instanceId, monsterInstanceId }) => {
      const selfBaseDef = getDefinition(state.instances[monsterInstanceId].definitionId);
      const otherKobolds = state.dungeonHall.filter((id) => id && id !== monsterInstanceId
        && getDefinition(state.instances[id].definitionId).group === selfBaseDef.group);
      const bonusHealth = otherKobolds.reduce((sum, id) => sum + (getDefinition(state.instances[id].definitionId).health || 0), 0);
      return applyInstanceOverride(state, instanceId, { health: (selfBaseDef.health || 0) + bonusHealth, chainKillGroup: selfBaseDef.group });
    },
  }],
});

// "Discard 1 equipped weapon" - a player can have several heroes each with
// their own equipped weapon, so this is a real choice among them, not just
// "the" equipped weapon (Matt: same "let me choose" issue). Candidates are
// restricted to currently-equipped weapon instances specifically, not the
// whole hand. No manual unequip step needed anymore - discardFromHand
// (effectHelpers.js) now clears a stale equippedWeapons entry automatically
// whenever the equipped card itself leaves the hand for any reason.
register('monster-ogrillon', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = Object.values(player.equippedWeapons).flat();
      return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Ogrillon: discard 1 equipped weapon.' });
    },
  }],
});

register('monster-ogre', {
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.strength || 0) <= 3;
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Ogre: destroy 1 hero with Strength 3 or less.' });
    },
  }],
});

register('monster-ettin', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player, (def) => !(def.abilityText.alwaysActive || [])
        .some((e) => /^Magic Attack/.test(e.text)));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Ettin: destroy 1 hero without Magic Attack.' });
    },
  }],
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        if (!isHeroLike(def)) return false;
        return !(def.abilityText.alwaysActive || []).some((e) => /^Physical Attack/.test(e.text));
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Ettin: destroy 1 hero without Physical Attack.' });
    },
  }],
});

register('monster-ogre-mage', {
  battle: [{
    // Target can be any card with a Magic Attack trait line, not just a hero
    // - Pike ("not affected by Battle effects") only excludes the specific
    // hero it's equipped to, not weapons/spells with Magic Attack.
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const hasMagicAttack = (id) => (getInstanceDefinition(state, id).abilityText.alwaysActive || []).some((e) => /^Magic Attack/.test(e.text));
      const candidateInstanceIds = excludeShieldedHeroes(state, player, player.hand.filter((id) => hasMagicAttack(id)));
      return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Ogre Mage: discard 1 card with Magic Attack.' });
    },
  }],
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.strength || 0) <= 4;
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Ogre Mage: destroy 1 hero with Strength 4 or less.' });
    },
  }],
});

// --- Undead: Horde (deferred mechanic, handlers kept for future use) -------

register('monster-soulstolen', {
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const equippedHeroIds = new Set(Object.keys(player.equippedWeapons));
      const candidateInstanceIds = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)) && !equippedHeroIds.has(id));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Soulstolen: destroy 1 unequipped hero.' });
    },
  }],
  // Trophy "Physical Attack +1 against undead" needs conditional-on-monster-
  // trait attack math this engine doesn't have yet (groupConditionalAttackBonuses
  // only keys on monster *group*, not a broader trait like "Undead") - left
  // unregistered rather than approximated incorrectly.
});

// --- Undead: Skeleton -----------------------------------------------------

register('monster-shrouded-cadaver', {
  battle: [{
    resolve: (state, { playerId }) => {
      let nextState = gainCurse(state, playerId);
      nextState = gainCurse(nextState, playerId);
      for (const id of otherPlayerIds(nextState, playerId)) nextState = gainCurse(nextState, id);
      return nextState;
    },
  }],
  trophy: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      if (choices?.decline) return state;
      const withoutCard = updatePlayer(state, playerId, (p) => ({
        ...p,
        hand: p.hand.filter((id) => id !== instanceId),
        discard: p.discard.filter((id) => id !== instanceId),
      }));
      return drawCards(withoutCard, playerId, 1);
    },
  }],
});

register('monster-dwarven-ancestor', {
  battle: [
    {
      resolve: (state, { playerId, hallRankIndex }) => {
        const baseDarkness = hallRankIndex + 1;
        const residualDarkness = baseDarkness - computeTotalLight(state, playerId);
        return residualDarkness > 0 ? gainCurse(state, playerId) : state;
      },
    },
    {
      // "If you defeat this card's Health by 4 or more, each other player
      // gains 1 curse" - evaluated against the final totalAttack, so it's
      // implemented as an aftermath-timed check even though the card prints
      // it under Battle (the outcome isn't known until after Battle
      // resolves); registered here as a battle handler that's a no-op, with
      // the real logic in the aftermath handler below to have `won`/margin data.
      resolve: (state) => state,
    },
  ],
  aftermath: [{
    resolve: (state, { playerId, won, totalAttack, monsterHealth }) => {
      if (!won || totalAttack < monsterHealth + 4) return state;
      let nextState = state;
      for (const id of otherPlayerIds(nextState, playerId)) nextState = gainCurse(nextState, id);
      return nextState;
    },
  }],
});

register('monster-necrophidius', {
  trophy: [{
    resolve: (state, { playerId, instanceId, choices }) => {
      if (choices?.decline) return state;
      const player = state.players.find((p) => p.id === playerId);
      // Only a legal target if its group's next-level stack actually has a
      // copy remaining - "may destroy this to level up" is entirely optional,
      // so an empty stack just means this Trophy has nothing to do (not an
      // error) rather than a hard requirement (unlike a Village/Dungeon
      // ability the player explicitly chose to use).
      const isLegalTarget = (id) => {
        if (id === instanceId) return false;
        const def = getInstanceDefinition(state, id);
        if (def.type !== 'hero' || def.level > 1) return false;
        const stack = state.village.heroStacks[def.group] || [];
        return stack.some((sid) => getInstanceDefinition(state, sid).level === def.level + 1);
      };
      const targetId = (choices?.heroInstanceId && isLegalTarget(choices.heroInstanceId))
        ? choices.heroInstanceId
        : player.hand.find(isLegalTarget);
      if (!targetId) return state;
      const withoutCard = updatePlayer(state, playerId, (p) => ({
        ...p,
        hand: p.hand.filter((id) => id !== instanceId),
        discard: p.discard.filter((id) => id !== instanceId),
      }));
      return performLevelUp(withoutCard, playerId, targetId, { payXp: false, destination: 'discard' });
    },
  }],
});

register('monster-warrior-bones', {
  trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
});

register('monster-ossuous', {
  trophy: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) }],
});

// --- Undead: Treefolk --------------------------------------------------

function marginAftermath(marginThreshold, onShortfall) {
  return (state, ctx) => {
    const { totalAttack, monsterHealth } = ctx;
    if (totalAttack >= monsterHealth + marginThreshold) return state;
    return onShortfall(state, ctx);
  };
}

register('monster-scabbark', {
  aftermath: [{
    resolve: marginAftermath(2, (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = state;
      for (const id of [...player.hand]) nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
      return nextState;
    }),
  }],
  spoils: [{
    // "Take another turn" - handled by not advancing activePlayerIndex; since
    // endTurn() always advances, this is approximated by granting the SAME
    // player another go via the meta.endTurnOverride-adjacent mechanism.
    resolve: (state) => ({ ...state, meta: { ...state.meta, extraTurnGranted: true } }),
  }],
});

register('monster-canker-treant', {
  aftermath: [{
    resolve: marginAftermath(6, (state, { playerId }) => updatePlayer(state, playerId, (p) => ({
      ...p, turnFlags: { ...p.turnFlags, handSizeReduction: p.turnFlags.handSizeReduction + 2 },
    }))),
  }],
});

register('monster-thorned-walker', {
  aftermath: [{
    // Matt: "I didn't have any monsters in my hand, but I was still forced
    // to destroy a card... I think I was supposed to have to destroy
    // nothing." Rulebook designer's note ("Thorned Walker"): "Monsters you
    // destroy must come from your hand, not the dungeon hall" - "revealed
    // monsters" means monster-type trophy cards currently in hand (hand
    // cards are "revealed" the moment you Enter the Dungeon/Visit the
    // Village - rulebook glossary, "Total Attack Value"), not a dedicated
    // reveal-from-deck step this card doesn't have. Destroys up to 2 (fewer
    // if you have less; correctly destroys nothing at all if you have none
    // - queueHandChoice already no-ops on an empty candidate list).
    resolve: marginAftermath(2, (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const monsterCardsInHand = player.hand.filter((id) => getInstanceDefinition(state, id).type === 'monster');
      const toDestroyCount = applyDoWhatYouCan(2, monsterCardsInHand).length;
      let nextState = state;
      for (let i = 0; i < toDestroyCount; i += 1) {
        nextState = queueHandChoice(nextState, {
          playerId, verb: 'destroy', candidateInstanceIds: monsterCardsInHand, reason: 'Thorned Walker: destroy 2 revealed monsters (monster cards in your hand).',
        });
      }
      return nextState;
    }),
  }],
});

register('monster-decayed-heartwood', {
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.strength || 0) >= 5;
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Decayed Heartwood: destroy a hero with 5 or more Strength.' });
    },
  }],
});

// ==========================================================================
// Caverns of Bane
// ==========================================================================

function countMonstersInHand(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => getInstanceDefinition(state, id).type === 'monster').length;
}

// --- Chiroptid --------------------------------------------------------------

register('monster-tenebrate', {
  // "Raid: If there is an Ambusher in the hall, destroy 1 card with a Light icon."
  raid: [{
    resolve: (state, { playerId }) => {
      if (!playerId || !anyAmbusherInHall(state)) return state;
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => (getInstanceDefinition(state, id).light || 0) > 0);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Tenebrate raids: destroy 1 card with a Light icon.' });
    },
  }],
});

register('monster-dusk-hunter', {
  // "Battle: Reveal the top card of your deck. Add that card's gold cost to
  // this card's Health." / "Aftermath: If you do not defeat this card,
  // destroy the revealed card and lose 2 XP." The Aftermath half needs to
  // know what Battle revealed - meta.duskHunterRevealedInstanceId is a
  // transient bridge between the two (cleared the moment Aftermath reads
  // it), the only CoB card needing this exact cross-timing shape so far.
  battle: [{
    resolve: (state, { playerId, monsterInstanceId }) => {
      const { state: revealedState, instanceId: revealedId } = revealTopOfOwnDeck(state, playerId);
      if (!revealedId) return revealedState;
      const revealedDef = getInstanceDefinition(revealedState, revealedId);
      const monsterDef = getInstanceDefinition(revealedState, monsterInstanceId);
      let nextState = applyInstanceOverride(revealedState, monsterInstanceId, { health: (monsterDef.health || 0) + (revealedDef.goldValue || 0) });
      nextState = { ...nextState, meta: { ...nextState.meta, duskHunterRevealedInstanceId: revealedId } };
      return nextState;
    },
  }],
  aftermath: [{
    resolve: (state, { playerId, won }) => {
      const revealedId = state.meta.duskHunterRevealedInstanceId;
      let nextState = { ...state, meta: { ...state.meta, duskHunterRevealedInstanceId: null } };
      if (won || !revealedId) return nextState;
      nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== revealedId) }));
      nextState = grantXp(nextState, playerId, -2);
      return nextState;
    },
  }],
});

// Desmodic's "Global: If there is an Ambusher in the hall, each time a
// player visits the village, that player destroys the top card of 1 hero
// stack" is implemented in this file's VILLAGE_VISIT_HALL_EFFECTS above
// (alongside Magma Wyrm's own "switches ranks" line), called from
// actions/index.js's VISIT_VILLAGE case - no register() entry needed since
// it isn't a hand-card-shaped ability.

register('monster-life-drinker', {
  // "Raid: If there is an Ambusher in the hall, lose 3 XP and destroy 1 card with a Light icon."
  raid: [{
    resolve: (state, { playerId }) => {
      if (!playerId || !anyAmbusherInHall(state)) return state;
      let nextState = grantXp(state, playerId, -3);
      const player = nextState.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => (getInstanceDefinition(nextState, id).light || 0) > 0);
      return queueHandChoice(nextState, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Life Drinker raids: destroy 1 card with a Light icon.' });
    },
  }],
});

register('monster-darkling', {
  // "Battle: Reveal the top card of your deck. Add that card's gold cost to
  // this card's Health. If the card has a Light icon, destroy it."
  battle: [{
    resolve: (state, { playerId, monsterInstanceId }) => {
      const { state: revealedState, instanceId: revealedId } = revealTopOfOwnDeck(state, playerId);
      if (!revealedId) return revealedState;
      const revealedDef = getInstanceDefinition(revealedState, revealedId);
      const monsterDef = getInstanceDefinition(revealedState, monsterInstanceId);
      let nextState = applyInstanceOverride(revealedState, monsterInstanceId, { health: (monsterDef.health || 0) + (revealedDef.goldValue || 0) });
      if ((revealedDef.light || 0) > 0) {
        nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== revealedId) }));
      }
      return nextState;
    },
  }],
});

// --- Doomknight Undead --------------------------------------------------
// Legion General's "Cannot be fought unless 2+ monsters are present" is
// implemented in dungeon/fightEligibility.js. Legion Warrior's "Immune to
// unequipped heroes" is implemented in combat/attackMath.js
// (MONSTER_IMMUNITIES's requiresEquippedHero field).

register('monster-legion-warrior', {
  // "Battle: Add Health +2 if at least 1 monster is present."
  battle: [{
    resolve: (state, { playerId, monsterInstanceId }) => {
      if (countMonstersInHand(state, playerId) < 1) return state;
      const monsterDef = getInstanceDefinition(state, monsterInstanceId);
      return applyInstanceOverride(state, monsterInstanceId, { health: (monsterDef.health || 0) + 2 });
    },
  }],
});

register('monster-legion-sorcerer', {
  // "Battle: Discard 1 hero or village card for each monster present."
  // Matt, confirming directly: Basic cards (Torch, Longspear, Thunderstone
  // Shard - basic-regular is already covered, isHeroLike matches it too)
  // share the same green border as the randomized village-row offer, so
  // they count as "village cards" here too - see heroes.js's own
  // Silvertongue comment for the fuller citation.
  battle: [{
    resolve: (state, { playerId }) => {
      const count = countMonstersInHand(state, playerId);
      let nextState = state;
      for (let i = 0; i < count; i += 1) {
        const player = nextState.players.find((p) => p.id === playerId);
        const candidateInstanceIds = excludeShieldedHeroes(nextState, player, player.hand.filter((id) => {
          const d = getInstanceDefinition(nextState, id);
          return isHeroLike(d) || d.type === 'village' || d.type === 'basic';
        }));
        nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Legion Sorcerer: discard 1 hero or village card for each monster present.' });
      }
      return nextState;
    },
  }],
});

register('monster-legion-necromancer', {
  // "Battle: Add Health +2 for each monster present."
  battle: [{
    resolve: (state, { playerId, monsterInstanceId }) => {
      const count = countMonstersInHand(state, playerId);
      if (count === 0) return state;
      const monsterDef = getInstanceDefinition(state, monsterInstanceId);
      return applyInstanceOverride(state, monsterInstanceId, { health: (monsterDef.health || 0) + 2 * count });
    },
  }],
});

// --- Elemental Salamander -------------------------------------------------

// "Draw 1 card. If it is not [a hero/a weapon/an item/a spell], add Health
// +2 [and destroy a matching card]." Diffs hand before/after to know what
// was actually drawn (drawCards itself doesn't report drawn ids) - same
// technique as Forcemage's draw-and-check in effectRegistry/heroes.js.
function salamanderDraw(isMatch, destroyCandidates) {
  return (state, { playerId, monsterInstanceId }) => {
    const before = new Set(state.players.find((p) => p.id === playerId).hand);
    let nextState = drawCards(state, playerId, 1);
    const drawnId = nextState.players.find((p) => p.id === playerId).hand.find((id) => !before.has(id));
    if (drawnId && isMatch(getInstanceDefinition(nextState, drawnId))) return nextState;
    const monsterDef = getInstanceDefinition(nextState, monsterInstanceId);
    nextState = applyInstanceOverride(nextState, monsterInstanceId, { health: (monsterDef.health || 0) + 2 });
    if (destroyCandidates) {
      const player = nextState.players.find((p) => p.id === playerId);
      const candidateInstanceIds = destroyCandidates(nextState, player);
      nextState = queueHandChoice(nextState, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Salamander: destroy a matching card.' });
    }
    return nextState;
  };
}

// Rulebook glossary (Caverns of Bane p.4): "Geyser Salamander: If multiple
// heroes have the highest Strength, choose 1 to destroy" - a real choice on
// ties, not an algorithmic pick (contrast Smokeplume Drake's ToR "highest
// Strength" precedent, which the rulebook never calls out as a tie-break
// choice the same way).
function highestStrengthHeroCandidates(state, player) {
  const eligibleIds = new Set(battleTargetableHeroes(state, player));
  const heroes = player.hand
    .map((id) => ({ id, def: getInstanceDefinition(state, id) }))
    .filter((c) => eligibleIds.has(c.id));
  if (!heroes.length) return [];
  const maxStrength = Math.max(...heroes.map((c) => c.def.strength || 0));
  return heroes.filter((c) => (c.def.strength || 0) === maxStrength).map((c) => c.id);
}

register('monster-geyser-salamander', {
  battle: [{ resolve: salamanderDraw((def) => isHeroLike(def), (state, player) => highestStrengthHeroCandidates(state, player)) }],
});
register('monster-steam-salamander', {
  battle: [{ resolve: salamanderDraw((def) => isHeroLike(def), null) }],
});
register('monster-molten-salamander', {
  battle: [{
    resolve: salamanderDraw(
      (def) => def.traits.includes('Weapon'),
      (state, player) => excludeShieldedHeroes(state, player, player.hand.filter((id) => {
        const d = getInstanceDefinition(state, id);
        return d.traits.includes('Fighter') || d.traits.includes('Weapon');
      })),
    ),
  }],
});
register('monster-fume-salamander', {
  battle: [{
    resolve: salamanderDraw(
      (def) => def.traits.includes('Item'),
      (state, player) => excludeShieldedHeroes(state, player, player.hand.filter((id) => {
        const d = getInstanceDefinition(state, id);
        return d.traits.includes('Thief') || d.traits.includes('Item');
      })),
    ),
  }],
});
register('monster-sulphur-salamander', {
  battle: [{
    resolve: salamanderDraw(
      (def) => def.traits.includes('Spell'),
      (state, player) => excludeShieldedHeroes(state, player, player.hand.filter((id) => {
        const d = getInstanceDefinition(state, id);
        return d.traits.includes('Wizard') || d.traits.includes('Spell');
      })),
    ),
  }],
});

// --- Ooze Cavern ------------------------------------------------------------
// Immunity/attack-type alwaysActive lines are in combat/attackMath.js's
// MONSTER_IMMUNITIES. Jelly Blebs has no timed ability at all (blank card
// beyond its own stats).

register('monster-slithering-ooze', {
  // "Aftermath: Destroy one hero of level 1 or lower."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.level ?? 1) <= 1;
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Slithering Ooze: destroy one hero of level 1 or lower.' });
    },
  }],
});

function destroyOneWeapon(reason) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
    return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason });
  };
}

register('monster-green-viscoid', { aftermath: [{ resolve: destroyOneWeapon('Green Viscoid: destroy 1 weapon.') }] });
register('monster-gelatinous-cube', { aftermath: [{ resolve: destroyOneWeapon('Gelatinous Cube: destroy 1 weapon.') }] });

register('monster-mucilaginous-mass', {
  // "Aftermath: Destroy 1 hero."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Mucilaginous Mass: destroy 1 hero.' });
    },
  }],
});

// --- Troll ------------------------------------------------------------------
// "Does not leave the hall after a player loses a combat against this
// card" is implemented in combat/resolveCombat.js's STAYS_IN_HALL_ON_LOSS.

// "Raid: The next player must enter the dungeon and fight this card." -
// state.meta.forcedFightObligation (createGameState.js) records who's
// obligated and against which specific monster instance. "The next player"
// is computed once, right here, off whoever is CURRENTLY active when this
// Raid resolves (the same player passed in as `playerId` - a hall refill's
// active player, rulebook: "the active player decides") - standard turn
// order (activePlayerIndex + 1) % players.length, same math as endTurn's
// own advance. Enforcement lives in actions/index.js (blocks
// VISIT_VILLAGE/PREPARE/REST for the obligated player) and
// actions/dungeonActions.js's chooseMonster (restricts which monster they
// may fight, and clears the obligation once they do - win or lose, "must
// fight" isn't "must defeat"). If Troll Skullcracker is no longer in the
// hall by the time it's actually that player's turn (defeated by someone
// else first, etc.), the obligation is simply treated as void wherever it's
// checked - nothing to hold them to.
register('monster-troll-skullcracker', {
  raid: [{
    resolve: (state, { instanceId, playerId }) => {
      const activeIndex = state.players.findIndex((p) => p.id === playerId);
      const nextPlayerId = state.players[(activeIndex + 1) % state.players.length].id;
      return { ...state, meta: { ...state.meta, forcedFightObligation: { playerId: nextPlayerId, monsterInstanceId: instanceId } } };
    },
  }],
});

// "Battle: Discard 1 hero unless a curse is present." - the whole Battle
// line was missing from source data entirely until the CoB stat audit
// found the card scan's Battle line had no corresponding field at all (see
// docs/expansion-audit.md's Caverns of Bane entry). "Present" read as
// in-hand, this engine's standing convention (same as monster-tendril-
// minion's own "if a disease is present" check just above).
register('monster-troll-osteomancer', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const hasCurse = player.hand.some((id) => getInstanceDefinition(state, id).traits.includes('Curse'));
      if (hasCurse) return state;
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Troll Osteomancer: discard 1 hero.' });
    },
  }],
});

register('monster-troll-bonebreaker', {
  battle: [
    { resolve: destroyOneWeapon('Troll Bonebreaker: discard 1 weapon.') }, // "Discard 1 weapon" - see note below
    { resolve: (state, { playerId }) => gainCurse(state, playerId) },
  ],
});
register('monster-troll-marrowgaunt', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(gainCurse(state, playerId), playerId) }],
});
register('monster-troll-spinecutter', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});

// --- Worm Vermin --------------------------------------------------------

register('monster-cavernmaw', {
  // "Battle: Destroy 1 hero or villager."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = excludeShieldedHeroes(state, player, player.hand.filter((id) => {
        const d = getInstanceDefinition(state, id);
        return isHeroLike(d) || d.traits.includes('Villager');
      }));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Cavernmaw: destroy 1 hero or villager.' });
    },
  }],
});

register('monster-mancatcher-worm', {
  // "Battle: Discard 1 hero."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Mancatcher Worm: discard 1 hero.' });
    },
  }],
});

register('monster-purple-worm', {
  // "Battle: Discard 2 cards or destroy 1 hero." A choice between two
  // entirely different EFFECTS, not just which card for one already-decided
  // effect - see effectHelpers.js's queueEffectChoice, the first card
  // needing that shape. Battle fires mid-combat, before the player is known
  // to be able to make a real up-front choice (same constraint as every
  // queueHandChoice use), so this queues a pending decision rather than
  // resolving inline; actions/handChoiceActions.js's EFFECT_CHOICE_RESOLVERS
  // has the actual "discard 2" / "destroy 1 hero" logic.
  battle: [{
    resolve: (state, { playerId }) => queueEffectChoice(state, {
      playerId,
      cardId: 'monster-purple-worm',
      options: [
        { key: 'discard', label: 'Discard 2 cards' },
        { key: 'destroy', label: 'Destroy 1 hero' },
      ],
      reason: 'Purple Worm: discard 2 cards or destroy 1 hero.',
    }),
  }],
  // "Aftermath: Destroy 1 hero." - a separate, unconditional hero-destroy on
  // top of the Battle choice above (the printed card has both lines; this
  // was missing from source data entirely until the CoB stat audit found
  // the card scan's Aftermath line had no corresponding field at all - see
  // docs/expansion-audit.md's Caverns of Bane entry). Same "destroy 1 hero,
  // player's choice among candidates" shape as Ogre/Ettin's own Aftermath
  // handlers just above.
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Purple Worm: destroy 1 hero.' });
    },
  }],
});

register('monster-albino-worm', {
  // "Battle: Attack +1 for each card with Light present." Ambiguous
  // Physical-or-Magic (unlike most flat Attack grants, this doesn't specify
  // either) - modeled as turnFlags.bonusTotalAttack, the same "unspecified
  // type" bucket Curse of Sloth's own ambiguous Attack reduction uses.
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const lightCount = player.hand.filter((id) => (getInstanceDefinition(state, id).light || 0) > 0).length;
      if (!lightCount) return state;
      return updatePlayer(state, playerId, (p) => ({
        ...p, turnFlags: { ...p.turnFlags, bonusTotalAttack: p.turnFlags.bonusTotalAttack + lightCount },
      }));
    },
  }],
});

register('monster-rot-grub', {
  // "Battle: Discard all but 1 hero." Queues (heroCount - 1) ordinary
  // "discard 1 hero" picks off the same full candidate list - each pick
  // naturally removes one hero from what's still actually in hand, so
  // after (heroCount - 1) resolutions exactly 1 remains, without needing a
  // new "choose 1 to keep" primitive.
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroIds = battleTargetableHeroes(state, player);
      let nextState = state;
      for (let i = 0; i < heroIds.length - 1; i += 1) {
        nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds: heroIds, reason: 'Rot Grub: discard all but 1 hero.' });
      }
      return nextState;
    },
  }],
  // "Raid: Choose 1 hero. If that hero's Attack Value is 5 or higher,
  // defeat this card (you gain the XP). Otherwise, destroy that hero." -
  // effectHelpers.js's queueAttackValueChoice, the first card whose outcome
  // depends on comparing a chosen hero's own stat against a threshold
  // rather than a fixed verb. Candidates are every hero-like card in hand
  // (not battleTargetableHeroes/Pike-filtered - Pike only protects against
  // Battle effects, and this is Raid).
  raid: [{
    resolve: (state, { instanceId, playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
      return queueAttackValueChoice(state, {
        playerId,
        monsterInstanceId: instanceId,
        threshold: 5,
        candidateInstanceIds,
        reason: 'Rot Grub: choose 1 hero - Attack Value 5 or higher defeats this card, otherwise that hero is destroyed.',
      });
    },
  }],
});

// ==========================================================================
// Root of Corruption
// ==========================================================================

function heroesPresent(state, playerId) {
  return state.players.find((p) => p.id === playerId).hand.filter((id) => isHeroLike(getInstanceDefinition(state, id)));
}

// "Each hero suffers Attack -N" - modeled as a flat reduction to the shared
// Total Attack Value pool (this engine tracks combined totals, not
// per-hero attack), sized to the number of heroes present - equivalent in
// total to reducing each of them individually. Same convention already
// used for Packhunter Gnoll/Decay Incarnate below.
function heroesSufferAttack(state, playerId, amount) {
  const count = heroesPresent(state, playerId).length;
  if (!count) return state;
  return { ...state, players: state.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, bonusTotalAttack: p.turnFlags.bonusTotalAttack - amount * count } } : p)) };
}

// Worlds Collide, Judgement: "Each hero suffers Physical Attack -1 [and
// Strength -2]." Same "flat reduction to the shared pool, sized to hero
// count" shape as heroesSufferAttack above, but keyed to the Physical-
// specific bonus pool (bonusPhysicalAttack) since the printed text
// specifies Physical, not ambiguous Attack.
function heroesSufferPhysicalAttack(state, playerId, amount) {
  const count = heroesPresent(state, playerId).length;
  if (!count) return state;
  return { ...state, players: state.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, bonusPhysicalAttack: p.turnFlags.bonusPhysicalAttack - amount * count } } : p)) };
}

// "Each hero suffers Strength -N" - see effectHelpers.js's
// grantTemporaryStrength (a negative amount, reset at end of turn same as
// every positive Strength grant).
function heroesSufferStrength(state, playerId, amount) {
  let nextState = state;
  for (const id of heroesPresent(nextState, playerId)) {
    nextState = grantTemporaryStrength(nextState, playerId, id, -amount);
  }
  return nextState;
}

// --- Djinnbound -----------------------------------------------------------

register('monster-minion-of-discord', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
  trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
});

register('monster-minion-of-decay', {
  battle: [
    {
      // "Add Health equal to the number of heroes present."
      resolve: (state, { playerId, monsterInstanceId }) => {
        const count = heroesPresent(state, playerId).length;
        if (!count) return state;
        const def = getInstanceDefinition(state, monsterInstanceId);
        return applyInstanceOverride(state, monsterInstanceId, { health: (def.health || 0) + count });
      },
    },
    { resolve: (state, { playerId }) => gainCurse(state, playerId) },
  ],
  trophy: [{
    // "Destroy 1 disease to draw 1 card."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const diseaseId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Disease'));
      if (!diseaseId) return state;
      return drawCards(destroyCurseFromHand(state, playerId, diseaseId), playerId, 1);
    },
  }],
});

register('monster-minion-of-horror', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});

register('monster-minion-of-war', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
  // "Aftermath: Destroy 1 card."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds: player.hand, reason: 'Minion of War: destroy 1 card.' });
    },
  }],
});

register('monster-minion-of-neglect', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
  // "Aftermath: Draw 1 fewer card when you refill your hand at the end of
  // the turn." Reuses the bonusHandSizeAtEndOfTurn field built for CoB's
  // Historian (the negative counterpart of its positive grant).
  aftermath: [{
    resolve: (state, { playerId }) => ({
      ...state,
      players: state.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, bonusHandSizeAtEndOfTurn: p.turnFlags.bonusHandSizeAtEndOfTurn - 1 } } : p)),
    }),
  }],
  trophy: [{ resolve: (state, { playerId }) => drawCards(state, playerId, 1) }],
});

// --- Elemental Earth --------------------------------------------------------
// Immunities (Rockslide/Aftershock/Eruptor/Scree/Tremorfiend's specialEffects
// lines) are in combat/attackMath.js's MONSTER_IMMUNITIES.

// "Breach: Destroy the top card of each [hero/weapon/spell] stack. Move
// this monster to the highest rank (once per turn)." Rockslide's own "move"
// clause (unlike Magma Wyrm's explicit "switches ranks with" - moveHallCard)
// uses effectHelpers.js's shiftHallCardToRank instead - Matt, confirming
// directly: a true relocate, with everyone between the old and new rank
// shifting by one to make room, not a swap. "Once per turn" is tracked via
// turnFlags.hallRepositionedInstanceIds (state/turnFlags.js) since Breach
// can in principle fire more than once for the same instance within a
// single turn (e.g. Greatsword's deferred multi-refill) - only the move
// itself is capped; the hero-stack destroy below has no such cap and still
// fires every time Breach does.
function destroyTopOfEachHeroStack(state) {
  let nextState = state;
  for (const group of Object.keys(nextState.village.heroStacks)) {
    const stack = nextState.village.heroStacks[group];
    if (!stack.length) continue;
    nextState = { ...nextState, village: { ...nextState.village, heroStacks: { ...nextState.village.heroStacks, [group]: stack.slice(1) } } };
  }
  return nextState;
}
function destroyTopOfEachVillageStackWithTrait(trait) {
  return (state) => {
    let nextState = state;
    for (const key of Object.keys(nextState.village.villageStacks)) {
      const stack = nextState.village.villageStacks[key];
      if (!stack.length) continue;
      const def = getInstanceDefinition(nextState, stack[0]);
      if (!def.traits.includes(trait)) continue;
      nextState = { ...nextState, village: { ...nextState.village, villageStacks: { ...nextState.village.villageStacks, [key]: stack.slice(1) } } };
    }
    return nextState;
  };
}

function rockslideMoveToHighestRank(state, { instanceId, playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  if (player.turnFlags.hallRepositionedInstanceIds.includes(instanceId)) return state;
  const fromRank = state.dungeonHall.indexOf(instanceId);
  if (fromRank === -1) return state; // already gone some other way
  const highestRank = HALL_RANK_COUNT[state.meta.boardSide] - 1;
  const moved = shiftHallCardToRank(state, fromRank, highestRank);
  return updatePlayer(moved, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, hallRepositionedInstanceIds: [...p.turnFlags.hallRepositionedInstanceIds, instanceId] },
  }));
}

register('monster-rockslide', {
  breach: [{
    resolve: (state, { instanceId, playerId }) => rockslideMoveToHighestRank(destroyTopOfEachHeroStack(state), { instanceId, playerId }),
  }],
});
register('monster-scree', { breach: [{ resolve: destroyTopOfEachVillageStackWithTrait('Weapon') }] });
register('monster-tremorfiend', { breach: [{ resolve: destroyTopOfEachVillageStackWithTrait('Spell') }] });

register('monster-aftershock', {
  // "Aftermath: Destroy 1 hero with Magic Attack." - a hero whose own
  // printed trait line grants Magic Attack (i.e. would show a nonzero
  // magicAttack via parseAlwaysActiveTraits), not merely "a Wizard."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && def.abilityText.alwaysActive.some((e) => /Magic Attack \+/.test(e.text));
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Aftershock: destroy 1 hero with Magic Attack.' });
    },
  }],
});

register('monster-eruptor', {
  // "Aftermath: Destroy the hero with the lowest Strength." No printed
  // tie-break text, but Matt (bug report, re: Fire Giant's identical
  // shape): "Whenever that is more than one possible target, I have to
  // be allowed to pick." Queues a choice among every hero tied at the
  // lowest Strength, rather than auto-picking the first one found -
  // same fix applied to destroyLowestStat below (Mountain Giant, Fire
  // Giant, Frost Giant's weapon clause all share this helper).
  aftermath: [{
    resolve: (state, { playerId }) => {
      const heroes = heroesPresent(state, playerId).map((id) => ({ id, def: getInstanceDefinition(state, id) }));
      if (!heroes.length) return state;
      const min = Math.min(...heroes.map((h) => h.def.strength || 0));
      const candidateInstanceIds = heroes.filter((h) => (h.def.strength || 0) === min).map((h) => h.id);
      return queueHandChoice(state, {
        playerId, verb: 'destroy', candidateInstanceIds, reason: 'Eruptor: destroy the hero with the lowest Strength.',
      });
    },
  }],
});

// --- Gnoll ------------------------------------------------------------------

// "Battle: Randomly select and reveal 3 cards from your discard pile. Each
// [weapon/item] revealed adds Health +1. Destroy one of the revealed
// [weapons/items]." - effectHelpers.js's pickRandomInstanceIds (random
// sampling without replacement) + queueDiscardPileChoice (the first "pick a
// card FROM DISCARD, not hand" choice in this engine). Revealed cards that
// AREN'T destroyed just stay in discard, untouched - "reveal" here is
// informational, unlike revealTopOfOwnDeck's own reveal-then-discard
// default. (Thieving Cur: weapons. Scavenging Cur: items.)
function gnollBattle(trait, title) {
  return (state, { playerId, monsterInstanceId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const { state: afterReveal, instanceIds: revealed } = pickRandomInstanceIds(state, player.discard, 3);
    if (!revealed.length) return afterReveal;
    const matching = revealed.filter((id) => getInstanceDefinition(afterReveal, id).traits.includes(trait));
    let nextState = afterReveal;
    if (matching.length) {
      const monsterDef = getInstanceDefinition(nextState, monsterInstanceId);
      nextState = applyInstanceOverride(nextState, monsterInstanceId, { health: (monsterDef.health || 0) + matching.length });
      nextState = queueDiscardPileChoice(nextState, {
        playerId, candidateInstanceIds: matching, reason: `${title}: destroy 1 of the revealed ${trait.toLowerCase()}s.`,
      });
    }
    // Matt: "I don't think the scavenging cur battle effect was triggered.
    // Maybe it ran in the background but there needs to be some sort of UI
    // feedback to show it happened." The reveal moves no cards (revealed
    // cards stay in discard), so the zone-diff log enrichment structurally
    // can't see it - and when none of the 3 revealed cards matches the
    // trait, the whole effect was 100% invisible. A real log line either
    // way, via the same pendingGlobalLogMessage channel resolveCombat's own
    // deferred outcome uses. Appended to any message already pending for
    // this player (Belac's battleRepeatCount can run this twice in one
    // fight) rather than overwriting it.
    const titles = revealed.map((id) => getInstanceDefinition(nextState, id).title).join(', ');
    const message = `revealed ${titles} from their discard pile (${title}: ${matching.length ? `Health +${matching.length}` : `no ${trait.toLowerCase()}s revealed`}).`;
    const pending = nextState.meta.pendingGlobalLogMessage;
    return {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingGlobalLogMessage: pending && pending.playerId === playerId
          ? { playerId, message: `${pending.message} ${message}` }
          : { playerId, message },
      },
    };
  };
}

register('monster-thieving-cur', { battle: [{ resolve: gnollBattle('Weapon', 'Thieving Cur') }] });
register('monster-scavenging-cur', { battle: [{ resolve: gnollBattle('Item', 'Scavenging Cur') }] });

register('monster-packhunter-gnoll', {
  // "Battle: If any Ambushers are in the hall, each hero suffers Attack -1."
  battle: [{
    resolve: (state, { playerId }) => (anyAmbusherInHall(state) ? heroesSufferAttack(state, playerId, 1) : state),
  }],
});

register('monster-alpha-gnoll', {
  // "Battle: Destroy 1 weapon."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Alpha Gnoll: destroy 1 weapon.' });
    },
  }],
});

// Marauder Gnoll: "Raid: If any Ambushers are in the hall, destroy 1 item,
// weapon, or spell." Missing from source data entirely until an audit pass
// caught it via a direct card-scan read (docs/expansion-audit.md) - same
// shape as monster-blackflock-assassin/monster-tenebrate above (a
// conditional Raid destroy, real player choice among matching candidates).
register('monster-marauder-gnoll', {
  raid: [{
    resolve: (state, { playerId }) => {
      if (!playerId || !anyAmbusherInHall(state)) return state;
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const traits = getInstanceDefinition(state, id).traits;
        return traits.includes('Item') || traits.includes('Weapon') || traits.includes('Spell');
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Marauder Gnoll raids: destroy 1 item, weapon, or spell.' });
    },
  }],
});

// --- Incarnate --------------------------------------------------------------

register('monster-discord-incarnate', {
  // "Battle: Reveal the top card of your deck. Add its gold cost to this
  // card's Health. If it has a gold cost, destroy it." Same shape as
  // Caverns of Bane's Dusk Hunter/Darkling.
  battle: [{
    resolve: (state, { playerId, monsterInstanceId }) => {
      const { state: revealedState, instanceId: revealedId } = revealTopOfOwnDeck(state, playerId);
      if (!revealedId) return revealedState;
      const revealedDef = getInstanceDefinition(revealedState, revealedId);
      const monsterDef = getInstanceDefinition(revealedState, monsterInstanceId);
      let nextState = applyInstanceOverride(revealedState, monsterInstanceId, { health: (monsterDef.health || 0) + (revealedDef.goldValue || 0) });
      if (revealedDef.goldValue !== undefined && revealedDef.goldValue !== null) {
        nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== revealedId) }));
      }
      return nextState;
    },
  }],
  // "Trophy: Cards cost 1 extra gold." Matt, confirming this reading
  // directly (and pointing at the rulebook's own "Trophy effects must be
  // used every time they are present in your hand" line): a per-turn
  // condition tied to hand presence, not a one-shot permanent effect -
  // implemented as a turnFlag (actions/villageActions.js's performBuy)
  // rather than a persistent village-level modifier.
  trophy: [{
    resolve: (state, { playerId }) => updatePlayer(state, playerId, (p) => ({
      ...p, turnFlags: { ...p.turnFlags, extraGoldCostPerCard: p.turnFlags.extraGoldCostPerCard + 1 },
    })),
  }],
});

// "specialEffects: Physical Attack required / Magic Attack required" (2
// lines) - combat/attackMath.js's MONSTER_IMMUNITIES requiresAttackType:
// 'both' field. Matt, correcting an earlier misreading: "required" is
// conditional (the other subtotal only counts if this one is ALSO
// present), not the same as "Immune to X Attack" (which unconditionally
// zeroes one subtotal regardless of the other) - "you have to have both of
// them in your attack," not "the attack is always voided."
register('monster-decay-incarnate', {
  trophy: [{ resolve: (state, { playerId }) => heroesSufferAttack(state, playerId, 1) }],
});

register('monster-horror-incarnate', {
  // "specialEffects: Add Health +2 if there is Darkness in this rank." An
  // alwaysActive line (not Battle-timed) - special-cased directly in
  // combat/resolveCombat.js (see HEALTH_BONUS_IF_DARKNESS there), same
  // reason Mowtil Djinni Lich's own Health-scaling special case exists.
  //
  // "Trophy Dungeon: Gain 1 curse." - a genuinely Dungeon-only Trophy (see
  // the rulebook FAQ, "What's a 'Trophy Dungeon'?": "This special Trophy
  // effect only applies in the Dungeon. Its effect does not happen in the
  // village, only when a player has chosen the enter the dungeon action.").
  // Registered under the 'trophyDungeon' timing (not 'trophy'), which
  // actions/abilityActions.js's resolveTrophyEffects only resolves when
  // called from ENTER_DUNGEON, never VISIT_VILLAGE.
  trophyDungeon: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});

register('monster-war-incarnate', {
  // "specialEffects: You can only select this monster to fight if you have
  // an equipped hero." - dungeon/fightEligibility.js FIGHT_ELIGIBILITY.
  // "Trophy: Ignore 1 hero with the highest Strength." Reuses this file's
  // own highestStrengthHeroCandidates (Geyser Salamander's precedent: ties
  // are a real choice, not an arbitrary pick) with effectHelpers.js's new
  // 'ignore' verb (markInstanceIgnored) instead of destroy/discard - the
  // card stays in hand, only its own contribution is voided.
  trophy: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = highestStrengthHeroCandidates(state, player);
      return queueHandChoice(state, { playerId, verb: 'ignore', candidateInstanceIds, reason: 'War Incarnate: ignore 1 hero with the highest Strength.' });
    },
  }],
});

register('monster-neglect-incarnate', {
  // "Battle: Each hero suffers Strength -3."
  battle: [{ resolve: (state, { playerId }) => heroesSufferStrength(state, playerId, 3) }],
  // "Aftermath: Destroy 1 hero with Strength lower than that hero's level."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.strength || 0) < (def.level || 0);
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Neglect Incarnate: destroy 1 hero with Strength lower than that hero\'s level.' });
    },
  }],
  // "Trophy: Discard down to 5 cards. You draw 1 fewer card when you draw
  // cards." Only the "discard down to 5" half is implemented - queues
  // (handSize - 5) ordinary discard picks off the live hand, the same
  // "each pick narrows what's left" technique as Rot Grub's "discard all
  // but 1 hero." The second half ("draw 1 fewer card" on every future
  // draw, not just the end-of-turn refill) would need a persistent, open-
  // ended hook on the generic drawCards path - broader than
  // bonusHandSizeAtEndOfTurn's end-of-turn-only scope. Flagged on
  // docs/expansion-audit.md's review list.
  trophy: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const excess = player.hand.length - 5;
      let nextState = state;
      for (let i = 0; i < excess; i += 1) {
        const current = nextState.players.find((p) => p.id === playerId);
        nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds: current.hand, reason: 'Neglect Incarnate: discard down to 5 cards.' });
      }
      return nextState;
    },
  }],
});

// --- Royal Guard ------------------------------------------------------------
// "Immune to Magic Attack" (Guard Captain) is in combat/attackMath.js's
// MONSTER_IMMUNITIES. Every "Each hero suffers Strength -N [and Attack -M]"
// line below is an alwaysActive specialEffects line (not Battle-timed) -
// special-cased in combat/resolveCombat.js (see ROYAL_GUARD_HERO_PENALTY
// there), the same reason Mowtil/Horror Incarnate's Health scaling is
// special-cased rather than registered here.

register('monster-huntsman', {
  // "Battle: Reveal the top card of the active player's deck. Each hero
  // suffers Strength penalty equal to its gold cost. If it is a weapon or
  // item, destroy it."
  battle: [{
    resolve: (state, { playerId }) => {
      const { state: revealedState, instanceId: revealedId } = revealTopOfOwnDeck(state, playerId);
      if (!revealedId) return revealedState;
      const revealedDef = getInstanceDefinition(revealedState, revealedId);
      let nextState = heroesSufferStrength(revealedState, playerId, revealedDef.goldValue || 0);
      if (revealedDef.traits.includes('Weapon') || revealedDef.traits.includes('Item')) {
        nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, discard: p.discard.filter((id) => id !== revealedId) }));
      }
      return nextState;
    },
  }],
});

register('monster-guardsman', {
  // "Aftermath: Destroy 1 fighter with 0 Strength." (checked AFTER the -6
  // Strength special case has already applied - see resolveCombat.js.)
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return def.traits.includes('Fighter') && (def.strength || 0) === 0;
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Guardsman: destroy 1 fighter with 0 Strength.' });
    },
  }],
});

register('monster-weapons-master', {
  // "Battle: Destroy any unequipped weapons." ("any" = all, not a choice.)
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const equippedIds = new Set(Object.values(player.equippedWeapons).flat());
      const unequippedWeaponIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon') && !equippedIds.has(id));
      let nextState = state;
      for (const id of unequippedWeaponIds) nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
      return nextState;
    },
  }],
});

register('monster-chiurgeon', {
  // "Aftermath: Destroy 1 hero with Strength less than that hero's level."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && (def.strength || 0) < (def.level || 0);
      });
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Chiurgeon: destroy 1 hero with Strength less than that hero\'s level.' });
    },
  }],
});

register('monster-guard-captain', {
  // "Battle: Destroy 1 unequipped hero."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const equippedIds = new Set(Object.keys(player.equippedWeapons));
      const candidateInstanceIds = battleTargetableHeroes(state, player).filter((id) => !equippedIds.has(id));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Guard Captain: destroy 1 unequipped hero.' });
    },
  }],
});

// ==========================================================================
// Numenera
// ==========================================================================

function classCount(state, playerId, heroClass) {
  return state.players.find((p) => p.id === playerId).hand
    .filter((id) => getInstanceDefinition(state, id).traits.includes(heroClass)).length;
}
// "Add [Physical/Magic] Attack +N to each [class]" - a per-count Trophy
// grant, same aggregate-pool convention as every other "each hero" effect
// in this file (the engine tracks combined totals, not per-hero attack).
function perClassTrophyBonus(heroClass, amount, type) {
  return (state, { playerId }) => {
    const count = classCount(state, playerId, heroClass);
    if (!count) return state;
    return type === 'magic' ? grantMagicAttack(state, playerId, amount * count) : grantPhysicalAttack(state, playerId, amount * count);
  };
}
function usedAbilityInstanceIds(state, playerId) {
  return state.players.find((p) => p.id === playerId).turnFlags.usedAbilityInstanceIds;
}
function hasUsedAbilityThisTurn(state, playerId, instanceId) {
  return usedAbilityInstanceIds(state, playerId).some((key) => key.startsWith(`${instanceId}:`));
}

// --- Abhuman ------------------------------------------------------------

register('monster-murden', {
  // "Trophy: If there is Darkness in this rank, add Physical Attack +2 to
  // each thief." The mandatory Trophy trigger (Murden present in hand) just
  // sets turnFlags.murdenTrophyActive; the actual Darkness condition and
  // the +2-per-Thief bonus are applied later, per fight, in
  // combat/attackMath.js's contributesTraits (using that fight's real
  // hasDarkness value - see turnFlags.js's own comment on this field).
  // "Suffer Light -1" (specialEffects) IS also implemented, special-cased
  // in combat/resolveCombat.js.
  trophy: [{
    resolve: (state, { playerId }) => updatePlayer(state, playerId, (p) => ({
      ...p, turnFlags: { ...p.turnFlags, murdenTrophyActive: true },
    })),
  }],
});
register('monster-chirog', {
  // "specialEffects: Immune to Magic Attack" - combat/attackMath.js MONSTER_IMMUNITIES.
  trophy: [{ resolve: perClassTrophyBonus('Wizard', 1, 'magic') }],
});
register('monster-margr', {
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }], // "Gain 1 disease" - every disease card is also a curse card in this engine's data model
  trophy: [{ resolve: perClassTrophyBonus('Cleric', 1, 'physical') }],
});
// "specialEffects: After you select this monster to fight, move it to rank
// 3." NOT YET IMPLEMENTED - a post-selection hall-repositioning effect, the
// same class of novelty as Root of Corruption's Elemental Earth "move to
// the highest rank" (also unimplemented). See docs/expansion-audit.md.
register('monster-sathosh', {
  trophy: [{ resolve: perClassTrophyBonus('Ranger', 1, 'physical') }],
});
register('monster-culova', {
  trophy: [{ resolve: perClassTrophyBonus('Fighter', 1, 'physical') }],
});

// --- Ancient Ones -----------------------------------------------------------
// Lavrik-ca's specialEffects ("Reduce your Total Attack Value to 0" /
// "Reduce this card's Health by 2 for each point of Light present") are
// both special-cased in combat/resolveCombat.js. Penumbra Fiend's
// specialEffects ("You cannot select this monster to fight if there is
// Darkness in this rank") is in actions/dungeonActions.js's chooseMonster.
// Both Penumbra Fiend and Philethis's own `light: -1` field (a Light
// penalty while fighting them, encoded as a plain stat rather than ability
// text) is also handled generically in resolveCombat.js.

register('monster-stellar-weaver', {
  // "Battle: If there is Darkness in this rank, add Health +4."
  battle: [{
    resolve: (state, { playerId, monsterInstanceId, hallRankIndex }) => {
      if (!hasDarknessAtRank(state, playerId, hallRankIndex)) return state;
      const def = getInstanceDefinition(state, monsterInstanceId);
      return applyInstanceOverride(state, monsterInstanceId, { health: (def.health || 0) + 4 });
    },
  }],
});
register('monster-philethis', {
  // "Battle: If you have 5 or more Light present, defeat this card."
  // Modeled as a Health-to-0 override (the cleanest way to force `won` true
  // without duplicating resolveCombat's own win/loss branching here).
  battle: [{
    resolve: (state, { playerId, monsterInstanceId }) => {
      if (computeTotalLight(state, playerId) < 5) return state;
      return applyInstanceOverride(state, monsterInstanceId, { health: 0 });
    },
  }],
});
register('monster-syzygy-ghoul', {
  // "Battle: Draw 1 card for each point of Light present."
  battle: [{
    resolve: (state, { playerId }) => {
      const lightCount = computeTotalLight(state, playerId);
      return lightCount ? drawCards(state, playerId, lightCount) : state;
    },
  }],
});

// --- Automatons ---------------------------------------------------------
// "Resolve this card's Battle effect twice if a Doomladen is present (in
// your hand)." (Disassembler/Mastigophore/Dark Fathom) -
// combat/resolveCombat.js's DOOMLADEN_DOUBLES_BATTLE.

register('monster-disassembler', {
  // "Battle: Destroy one mercenary or level 1 hero."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = excludeShieldedHeroes(state, player, player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        return def.traits.includes('Mercenary') || (isHeroLike(def) && (def.level ?? 1) === 1);
      }));
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Disassembler: destroy one mercenary or level 1 hero.' });
    },
  }],
});
// battleHeroes: true opts a caller into the Pike/Rage of the Disowned
// Battle-timed shield (effectHelpers.js's excludeShieldedHeroes) - only
// correct for BATTLE-timed registrations whose `matches` predicate can
// select a hero (Aftermath isn't shielded by either card; non-hero-only
// predicates like Guardian Automaton's "Monster present" have no hero to
// shield in the first place, so they leave it off).
function destroyByTrait(reason, matches, { battleHeroes = false } = {}) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    let candidateInstanceIds = player.hand.filter((id) => matches(getInstanceDefinition(state, id)));
    if (battleHeroes) candidateInstanceIds = excludeShieldedHeroes(state, player, candidateInstanceIds);
    return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason });
  };
}
function discardByTrait(reason, matches, { battleHeroes = false } = {}) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    let candidateInstanceIds = player.hand.filter((id) => matches(getInstanceDefinition(state, id)));
    if (battleHeroes) candidateInstanceIds = excludeShieldedHeroes(state, player, candidateInstanceIds);
    return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason });
  };
}
register('monster-zhev', { battle: [{ resolve: destroyByTrait('Zhev: destroy 1 thief.', (def) => def.traits.includes('Thief'), { battleHeroes: true }) }] });
register('monster-mastigophore', { battle: [{ resolve: destroyByTrait('Mastigophore: destroy 1 human hero.', (def) => isHeroLike(def) && def.traits.includes('Human'), { battleHeroes: true }) }] });
register('monster-dark-fathom', {
  battle: [{ resolve: destroyByTrait('Dark Fathom: destroy 1 wizard or cleric.', (def) => def.traits.includes('Wizard') || def.traits.includes('Cleric'), { battleHeroes: true }) }],
});
register('monster-guardian-automaton', {
  // "Battle: Destroy 1 monster present." / "specialEffects: Add Health +4
  // if a monster is present." Monster-type cards essentially never sit in
  // hand under normal play (see effectHelpers.js's isHeroLike comment) -
  // both lines are correct as written even though they'll rarely trigger.
  battle: [{ resolve: destroyByTrait('Guardian Automaton: destroy 1 monster present.', (def) => def.type === 'monster') }],
});

// --- Beasts -----------------------------------------------------------------

register('monster-blood-barm', {
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }], // "Gain 1 disease" ("Suffer Light -1" is special-cased in resolveCombat.js)
});
register('monster-thuman', {
  // "Battle: Discard 1 hero."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Thuman: discard 1 hero.' });
    },
  }],
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});
register('monster-broken-hound', {
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});
register('monster-ithsyn', {
  // "specialEffects: Immune to weapons other than bows." - combat/attackMath.js MONSTER_IMMUNITIES.
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});
register('monster-seskii', {
  // "Battle: If a weapon is equipped, gain 1 disease."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      return Object.keys(player.equippedWeapons).length ? gainCurse(state, playerId) : state;
    },
  }],
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});

// --- Bandit -------------------------------------------------------------------

// "Battle: Destroy 1 [spell/weapon or item] with an ability you have used."
// / "Destroy all items, weapons, and spells with abilities you have used."
// Reuses the acting player's own turnFlags.usedAbilityInstanceIds (already
// tracked for the once-per-turn ability cap) to identify which specific
// cards qualify - needs both the card def AND the player's own used-ability
// list, so this is its own helper rather than reusing destroyByTrait above
// (which only sees the def).
function destroyUsedCardWithTrait(reason, matches) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidateInstanceIds = player.hand.filter((id) => matches(getInstanceDefinition(state, id)) && hasUsedAbilityThisTurn(state, playerId, id));
    return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason });
  };
}

register('monster-mounted-raider', {
  battle: [
    { resolve: destroyUsedCardWithTrait('Mounted Raider: destroy 1 spell with an ability you have used.', (def) => def.traits.includes('Spell')) },
    {
      resolve: destroyUsedCardWithTrait(
        'Mounted Raider: destroy 1 weapon or item with an ability you have used.',
        (def) => def.traits.includes('Weapon') || def.traits.includes('Item'),
      ),
    },
  ],
});
register('monster-bandit-taskmaster', {
  // "Battle: Place 1 weapon and 1 item on top of your deck." "Global: You
  // must use all Dungeon abilities (if possible)." NOT YET IMPLEMENTED for
  // the Global half - forcing a player to exhaust every possible Dungeon
  // ability before ending their turn is an obligation-tracking mechanic
  // (verifying "if possible" against every hand card's current legality),
  // not a single discrete effect. See docs/expansion-audit.md.
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const weaponId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      const itemId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Item') && id !== weaponId);
      let nextState = state;
      for (const id of [weaponId, itemId].filter(Boolean)) {
        nextState = updatePlayer(nextState, playerId, (p) => ({ ...p, hand: p.hand.filter((h) => h !== id), deck: [id, ...p.deck] }));
      }
      return nextState;
    },
  }],
});
register('monster-bandit-killer', {
  // "Battle: Place 1 item on top of your deck."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const itemId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Item'));
      if (!itemId) return state;
      return updatePlayer(state, playerId, (p) => ({ ...p, hand: p.hand.filter((h) => h !== itemId), deck: [itemId, ...p.deck] }));
    },
  }],
});
register('monster-bandit-warlord', {
  // "Battle: Destroy all items, weapons, and spells with abilities you have used."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetIds = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        const isTargetTrait = def.traits.includes('Item') || def.traits.includes('Weapon') || def.traits.includes('Spell');
        return isTargetTrait && hasUsedAbilityThisTurn(state, playerId, id);
      });
      let nextState = state;
      for (const id of targetIds) nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
      return nextState;
    },
  }],
});
// Bandit Warrior's only ability is a "Global: You must use all Dungeon
// abilities (if possible)." line - same unimplemented obligation-tracking
// mechanic as Bandit Taskmaster's Global above.

// --- Horror -------------------------------------------------------------------
// Night Howler/Edacious Destroyer's "Immune to Magic Attack or Physical
// Attack, whichever is [higher/lower]" and Erynth Grask/Tetrahydra's
// "Magic Attack Required" are all in combat/attackMath.js's
// MONSTER_IMMUNITIES.

register('monster-tetrahydra', {
  // "Battle: Discard 1 weapon."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      return queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Tetrahydra: discard 1 weapon.' });
    },
  }],
});

// --- Infected -----------------------------------------------------------------
// "Doomladen" is a trait, checked the same way as every other trait via
// effectHelpers.js's hasTraitPresent - not a new mechanic, just a new
// keyword name for this set.

register('monster-tactile-host', {
  // "Battle: Place 1 Doomladen present on the bottom of the dungeon deck."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const doomladenId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Doomladen'));
      if (!doomladenId) return state;
      let nextState = updatePlayer(state, playerId, (p) => ({ ...p, hand: p.hand.filter((id) => id !== doomladenId) }));
      return { ...nextState, dungeonDeck: [...nextState.dungeonDeck, doomladenId] };
    },
  }],
});
// Terror Bird: "Global: If a Doomladen is present, you must select a Terror
// Bird to fight." NOT YET IMPLEMENTED - a fight-restriction that blocks
// choosing OTHER hall monsters (not just gating itself, the way every
// FIGHT_ELIGIBILITY entry does) - broader than that table's shape. See
// docs/expansion-audit.md.
register('monster-ravage-bear', {
  // "Battle: Each hero suffers Physical Attack -1 for each monster present."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const monsterCount = player.hand.filter((id) => getInstanceDefinition(state, id).type === 'monster').length;
      const heroCount = heroesPresent(state, playerId).length;
      if (!monsterCount || !heroCount) return state;
      return updatePlayer(state, playerId, (p) => ({ ...p, turnFlags: { ...p.turnFlags, bonusTotalAttack: p.turnFlags.bonusTotalAttack - monsterCount * heroCount } }));
    },
  }],
});
register('monster-warrior-host', {
  // "Battle: If a Doomladen is present, gain 2 diseases."
  battle: [{
    resolve: (state, { playerId }) => (hasTraitPresent(state, playerId, 'Doomladen') ? gainCurse(gainCurse(state, playerId), playerId) : state),
  }],
});
register('monster-ocular-host', {
  // "Raid: If a Doomladen is present, place it on the bottom of the dungeon deck."
  raid: [{
    resolve: (state, { playerId }) => {
      if (!playerId) return state;
      const player = state.players.find((p) => p.id === playerId);
      const doomladenId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Doomladen'));
      if (!doomladenId) return state;
      let nextState = updatePlayer(state, playerId, (p) => ({ ...p, hand: p.hand.filter((id) => id !== doomladenId) }));
      return { ...nextState, dungeonDeck: [...nextState.dungeonDeck, doomladenId] };
    },
  }],
});

// --- Monstrosity --------------------------------------------------------------

register('monster-callerail', {
  battle: [{ resolve: destroyByTrait('Callerail: destroy 1 hero.', (def) => isHeroLike(def), { battleHeroes: true }) }],
  aftermath: [{ resolve: destroyByTrait('Callerail: destroy 1 weapon.', (def) => def.traits.includes('Weapon')) }],
});
register('monster-mesomeme', {
  battle: [{ resolve: destroyByTrait('Mesomeme: destroy 1 item.', (def) => def.traits.includes('Item')) }],
  aftermath: [{ resolve: destroyByTrait('Mesomeme: destroy 1 hero.', (def) => isHeroLike(def)) }],
});
register('monster-cragworm', {
  battle: [{ resolve: destroyByTrait('Cragworm: destroy 1 hero.', (def) => isHeroLike(def), { battleHeroes: true }) }],
  aftermath: [{ resolve: destroyByTrait('Cragworm: destroy 1 hero.', (def) => isHeroLike(def)) }],
});
register('monster-travonis-ul', {
  // "Battle: Discard 2 heroes."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      let nextState = queueHandChoice(state, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Travonis Ul: discard 2 heroes.' });
      nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds, reason: 'Travonis Ul: discard 2 heroes.' });
      return nextState;
    },
  }],
});
register('monster-jiriskar', {
  battle: [{ resolve: discardByTrait('Jiriskar: discard 1 hero.', (def) => isHeroLike(def), { battleHeroes: true }) }],
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const weaponId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Weapon'));
      let nextState = weaponId ? destroyFromHand(state, playerId, weaponId, { battleEffect: true }) : state;
      const itemId = nextState.players.find((p) => p.id === playerId).hand.find((id) => getInstanceDefinition(nextState, id).traits.includes('Item'));
      if (itemId) nextState = destroyFromHand(nextState, playerId, itemId, { battleEffect: true });
      return nextState;
    },
  }],
});

// --- Ultraterrestrial -----------------------------------------------------
// "You cannot defeat this card with N or higher Total Attack Value" /
// "...unless exactly N" - combat/attackMath.js's DEFEAT_CONDITIONS,
// checked in combat/resolveCombat.js alongside the normal Health
// comparison. No other ability text on any card in this group.

register('monster-ellnoica', {
  battle: [{ resolve: discardByTrait('Ellnoica: discard 1 hero.', (def) => isHeroLike(def), { battleHeroes: true }) }],
});

// --- Verminous ------------------------------------------------------------

register('monster-steel-spider', {
  battle: [{ resolve: destroyByTrait('Steel Spider: destroy 1 weapon.', (def) => def.traits.includes('Weapon')) }],
  trophy: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) }],
});
register('monster-scutimorph', {
  battle: [{ resolve: destroyByTrait('Scutimorph: destroy 1 hero.', (def) => isHeroLike(def), { battleHeroes: true }) }],
});
register('monster-slicer-beetle', {
  battle: [{ resolve: destroyByTrait('Slicer Beetle: destroy 1 item.', (def) => def.traits.includes('Item')) }],
});
register('monster-laak', {
  trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
});

// ==========================================================================
// Starter set
// ==========================================================================

// --- Behemoth ---------------------------------------------------------------
// Hulking Yeti's "Magic Attack Required" is in combat/attackMath.js's
// MONSTER_IMMUNITIES.

register('monster-red-gronek', { battle: [{ resolve: destroyByTrait('Red Gronek: destroy 1 item.', (def) => def.traits.includes('Item')) }] });
register('monster-blue-gronek', { aftermath: [{ resolve: destroyByTrait('Blue Gronek: destroy 1 weapon.', (def) => def.traits.includes('Weapon')) }] });
// Matt reported this directly: "The cyclonic [sic] horror battle effect
// wants me to discard a village card for my hand, but it's not allowing
// me to discard any village cards. I do have a torch in my hand..." -
// confirmed on follow-up that Basic cards share the green border that
// makes something a "village card" per the rulebook glossary (see
// heroes.js's own Silvertongue comment for the fuller citation) - this
// excluded them, same gap Legion Sorcerer/Silvertongue both had too.
register('monster-cyclopic-horror', { battle: [{ resolve: discardByTrait('Cyclopic Horror: discard 1 village card.', (def) => def.type === 'village' || def.type === 'basic') }] });
// Faceless Colossus has no ability text at all - no registration needed.

// --- Bestial ------------------------------------------------------------

register('monster-ophidian-cutter', { trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }] });
register('monster-gnoll-ravager', {
  // "Trophy: Add Strength +2 to one Hero." No tie-break text - defaults to
  // the first hero present, mirroring other single-target auto-picks
  // elsewhere in this file.
  trophy: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroId = player.hand.find((id) => isHeroLike(getInstanceDefinition(state, id)));
      if (!heroId) return state;
      return grantTemporaryStrength(state, playerId, heroId, 2);
    },
  }],
});
register('monster-kobold-meddler', { battle: [{ resolve: discardByTrait('Kobold Meddler: discard 1 weapon.', (def) => def.traits.includes('Weapon')) }] });
register('monster-leonine-shredder', { aftermath: [{ resolve: destroyByTrait('Leonine Shredder: destroy 1 hero.', (def) => isHeroLike(def)) }] });
register('monster-orc-spiker', {
  battle: [{ resolve: destroyByTrait('Orc Spiker: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
  // "Trophy: Magic Attack +1." - missing from source data entirely until the
  // Starter Set audit (2026-07-22); confirmed against the card scan, which
  // clearly prints both lines. Same "field missing from both the scan-
  // reading pass and source data" shape as Thundermage Bolter's missing
  // `light: 2` in the base ToR audit - see docs/card-audit.md.
  trophy: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) }],
});

// --- Virulent -------------------------------------------------------------
// Vampiric Minion's "Immune to Regulars" is in combat/attackMath.js's
// MONSTER_IMMUNITIES.

register('monster-poison-skeleton', { battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }] });
register('monster-zombie-mob', {
  battle: [
    { resolve: (state, { playerId }) => gainCurse(state, playerId) },
    {
      // "This card gains Health +1 for each Undead revealed in your hand."
      resolve: (state, { playerId, monsterInstanceId }) => {
        const undeadCount = state.players.find((p) => p.id === playerId).hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Undead')).length;
        if (!undeadCount) return state;
        const def = getInstanceDefinition(state, monsterInstanceId);
        return applyInstanceOverride(state, monsterInstanceId, { health: (def.health || 0) + undeadCount });
      },
    },
  ],
});
register('monster-filth-queen', { battle: [{ resolve: (state, { playerId }) => gainCurse(gainCurse(state, playerId), playerId) }] });
register('monster-bloodbound', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
  aftermath: [{ resolve: destroyByTrait('Bloodbound: destroy 1 hero.', (def) => isHeroLike(def)) }],
});

// ==========================================================================
// Into the Abyss
// ==========================================================================
// "Halve Total Attack Value (round down) unless..." (Tormentor/Succubus) -
// combat/resolveCombat.js's HALVE_UNLESS, applied to the fully-assembled
// totalAttack right alongside Lavrik-ca's own "reduce to 0" override.

// --- Abyssal Elite ----------------------------------------------------------

register('monster-archduke-of-pain', {
  // "specialEffects: Magic Attack required." - combat/attackMath.js MONSTER_IMMUNITIES.
  aftermath: [{ resolve: destroyByTrait('Archduke of Pain: destroy each cleric and wizard.', (def) => def.traits.includes('Cleric') || def.traits.includes('Wizard')) }],
  breach: [{
    // "Destroy the top 2 cards of each hero stack." Every non-empty hero
    // stack in the village, not the acting player's hand - same
    // village.heroStacks[...] shift pattern as hero-tough-l2/l3's own
    // "place the top card of a hero stack" abilities, but destroying
    // (removing entirely) rather than moving to discard.
    resolve: (state) => {
      let nextState = state;
      for (const [groupKey, stack] of Object.entries(nextState.village.heroStacks)) {
        if (!stack.length) continue;
        nextState = { ...nextState, village: { ...nextState.village, heroStacks: { ...nextState.village.heroStacks, [groupKey]: stack.slice(2) } } };
      }
      return nextState;
    },
  }],
});
register('monster-tormentor', {
  // "specialEffects: Halve Total Attack Value (round down) unless a weapon
  // is equipped." - combat/resolveCombat.js HALVE_UNLESS.
  aftermath: [{ resolve: destroyByTrait('Tormentor: destroy 1 cleric.', (def) => def.traits.includes('Cleric')) }],
});
register('monster-succubus', {
  // "specialEffects: Halve Total Attack Value (round down) unless Magic
  // Attack is present." - combat/resolveCombat.js HALVE_UNLESS.
});
register('monster-the-unchained', {
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
  trophy: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 1) }],
});

// --- Abyssal Malformed --------------------------------------------------------

register('monster-death-polyps', {
  // "Aftermath: Destroy 1 monster. Gain 1 disease for each XP that monster
  // was worth." Monster-type cards essentially never sit in hand under
  // normal play (see effectHelpers.js's isHeroLike comment on the same
  // point) - correct as written even though it will rarely trigger.
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = player.hand.find((id) => getInstanceDefinition(state, id).type === 'monster');
      if (!targetId) return state;
      const xp = getInstanceDefinition(state, targetId).xp || 0;
      let nextState = destroyFromHand(state, playerId, targetId, { battleEffect: true });
      for (let i = 0; i < xp; i += 1) nextState = gainCurse(nextState, playerId);
      return nextState;
    },
  }],
});
register('monster-ghost-toxin', {
  aftermath: [{
    // "Destroy 1 Light item. Gain 1 disease if a Light item is destroyed."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = player.hand.find((id) => {
        const t = getInstanceDefinition(state, id).traits || [];
        return t.includes('Item') && t.includes('Light');
      });
      if (!targetId) return state;
      return gainCurse(destroyFromHand(state, playerId, targetId, { battleEffect: true }), playerId);
    },
  }],
});
register('monster-snakewyrm', {
  // "specialEffects: Add Health +1 for each other Doomspawn in the hall." -
  // this source-data line was actually a mistagged copy of Doomspawn's own
  // text (see buildCardData.mjs's stripMisattributedSnakewyrmText) - both
  // physical copies collapse to the same definition with NO specialEffects
  // line at all, so nothing to implement or flag here.
  aftermath: [{
    // "Destroy 2 Regulars. Gain 1 disease for each Regular destroyed."
    // "destroyed" counted via the stats.cardsDestroyed delta, not per
    // attempted target - with Ambrosia's redirect up the Regular is
    // DISCARDED instead, and a discarded Regular earns no disease.
    resolve: (state, { playerId }) => {
      let nextState = state;
      const destroyedBefore = state.players.find((p) => p.id === playerId).stats.cardsDestroyed;
      for (let i = 0; i < 2; i += 1) {
        const current = nextState.players.find((p) => p.id === playerId);
        const targetId = current.hand.find((id) => getInstanceDefinition(nextState, id).id === 'basic-regular');
        if (!targetId) break;
        nextState = destroyFromHand(nextState, playerId, targetId, { battleEffect: true });
      }
      const destroyedCount = nextState.players.find((p) => p.id === playerId).stats.cardsDestroyed - destroyedBefore;
      for (let i = 0; i < destroyedCount; i += 1) nextState = gainCurse(nextState, playerId);
      return nextState;
    },
  }],
});
register('monster-spike-lasher', {
  aftermath: [{
    // "Destroy all food. Gain 1 disease for each food destroyed."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const foodIds = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Food'));
      let nextState = state;
      for (const id of foodIds) nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
      for (let i = 0; i < foodIds.length; i += 1) nextState = gainCurse(nextState, playerId);
      return nextState;
    },
  }],
});
register('monster-spider-bloat', {
  aftermath: [{
    // "Destroy 1 fighter. Gain 1 disease for each level of that fighter."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const targetId = player.hand.find((id) => getInstanceDefinition(state, id).traits.includes('Fighter'));
      if (!targetId) return state;
      const level = getInstanceDefinition(state, targetId).level || 1;
      let nextState = destroyFromHand(state, playerId, targetId, { battleEffect: true });
      for (let i = 0; i < level; i += 1) nextState = gainCurse(nextState, playerId);
      return nextState;
    },
  }],
});

// --- Elemental Pain -----------------------------------------------------------
// Smoke Torment/Shadow Torment's "specialEffects: Immune to Physical Attack" -
// combat/attackMath.js MONSTER_IMMUNITIES (same voidAttackType mechanism as
// Cavernmaw's own "Immune to Physical Attack," worded identically).

// "All players reveal the top 5 cards from their decks. Each player destroys
// 1 revealed card matching `matches` (if any). Discard the rest." Shared by
// every Elemental Pain Breach effect below - each player resolves their own
// reveal/destroy independently (same "every player, not just the active
// one" convention as Charred Bruin/Blazing Grimalkin's own Battle effects).
function revealTopFiveDestroyMatching(matches) {
  return (state) => {
    let nextState = state;
    for (const p of state.players) {
      const revealed = [];
      for (let i = 0; i < 5; i += 1) {
        const { state: afterReveal, instanceId } = revealTopOfOwnDeck(nextState, p.id);
        nextState = afterReveal;
        if (instanceId) revealed.push(instanceId);
      }
      const targetId = revealed.find((id) => matches(getInstanceDefinition(nextState, id)));
      if (targetId) {
        // Revealed cards already sit in discard (revealTopOfOwnDeck's own
        // convention) - "destroy" strips it out of there directly, same
        // approach as village-demon-vision's own reveal-and-destroy handler.
        nextState = { ...nextState, players: nextState.players.map((pp) => (pp.id === p.id ? { ...pp, discard: pp.discard.filter((id) => id !== targetId) } : pp)) };
      }
    }
    return nextState;
  };
}

register('monster-blood-torment', {
  // "Battle: Each player shows his or her hand and destroys 1 revealed
  // monster (if any)." Monster-type cards essentially never sit in hand
  // under normal play - correct as written even though it will rarely
  // trigger.
  battle: [{
    resolve: (state) => {
      let nextState = state;
      for (const p of state.players) {
        const current = nextState.players.find((pp) => pp.id === p.id);
        const targetId = current.hand.find((id) => getInstanceDefinition(nextState, id).type === 'monster');
        if (targetId) nextState = destroyFromHand(nextState, p.id, targetId, { battleEffect: true });
      }
      return nextState;
    },
  }],
  breach: [{ resolve: revealTopFiveDestroyMatching((def) => (def.points || 0) > 0) }],
});
register('monster-steam-torment', {
  // "Global: Monsters will not leave the hall unless defeated." Implemented
  // in combat/resolveCombat.js's anySteamTormentInHall, folded into the
  // existing Guardian/Troll "never retreats on a loss" branch as a third
  // condition - see effectRegistryCoverage.test.js's KNOWN_GAPS entry for
  // 'monster-steam-torment:global:0'.
  trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
});
register('monster-lava-torment', {
  breach: [{ resolve: revealTopFiveDestroyMatching((def) => isHeroLike(def) && (def.level ?? 1) === 1) }],
});
register('monster-smoke-torment', {
  breach: [{ resolve: revealTopFiveDestroyMatching((def) => def.id === 'basic-regular') }],
});
register('monster-shadow-torment', {
  breach: [{ resolve: revealTopFiveDestroyMatching((def) => isHeroLike(def) && (def.level ?? 1) === 2) }],
});

// --- Undead Lich --------------------------------------------------------------
// Every card in this group: "specialEffects: Immune to Magic Attack." -
// combat/attackMath.js MONSTER_IMMUNITIES.

register('monster-lich-lord', {
  // "Battle: Each hero suffers Attack -1 and Strength -4." Same
  // alwaysActive-adjacent "each hero suffers" shape already special-cased
  // for Root of Corruption's Royal Guard - but THIS one is Battle-timed (a
  // real registry handler), not alwaysActive, so it belongs here rather
  // than resolveCombat.js's ROYAL_GUARD_STRENGTH_PENALTY table.
  battle: [{
    resolve: (state, { playerId }) => {
      // Strength via heroesSufferStrength -> grantTemporaryStrength, NOT an
      // inline instances[].strength write: the inline form never registered
      // in turnFlags.temporaryStrengthBoostInstanceIds, so endTurn's revert
      // loop couldn't strip it and the -4 stuck for the rest of the game -
      // the exact regression class turnFlags.js documents ("it should go
      // away after the turn ends"). Revenant already does it this way.
      const nextState = heroesSufferStrength(state, playerId, 4);
      return {
        ...nextState,
        players: nextState.players.map((p) => (p.id === playerId ? { ...p, turnFlags: { ...p.turnFlags, bonusTotalAttack: p.turnFlags.bonusTotalAttack - 1 } } : p)),
      };
    },
  }],
  aftermath: [{ resolve: destroyByTrait('Lich Lord: destroy each hero with Strength 0.', (def) => isHeroLike(def) && (def.strength || 0) === 0) }],
});
register('monster-tomb-haunt', { aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }] });
register('monster-deathbringer', { aftermath: [{ resolve: destroyByTrait('Deathbringer: destroy 1 spell.', (def) => def.traits.includes('Spell')) }] });
register('monster-destiny', {
  aftermath: [{ resolve: destroyByTrait('Destiny: destroy 1 cleric or wizard.', (def) => def.traits.includes('Cleric') || def.traits.includes('Wizard')) }],
});
// Grave Knight has no ability text beyond the group's shared "Immune to
// Magic Attack" - no registration needed.

// --- Undead Plague --------------------------------------------------------------

register('monster-walking-scourge', {
  battle: [{
    // "Gain 2 diseases. Each other player gains 1 disease."
    resolve: (state, { playerId }) => {
      let nextState = gainCurse(gainCurse(state, playerId), playerId);
      for (const p of state.players) {
        if (p.id !== playerId) nextState = gainCurse(nextState, p.id);
      }
      return nextState;
    },
  }],
  breach: [{
    resolve: (state) => {
      let nextState = state;
      for (const p of state.players) nextState = gainCurse(nextState, p.id);
      return nextState;
    },
  }],
});
register('monster-plaguebearer', {
  // "Global: Each player gains 1 disease when another monster is
  // defeated." Implemented in combat/resolveCombat.js's
  // anyPlaguebearerInHall, fired right after a fight's own win/loss
  // bookkeeping is applied - see effectRegistryCoverage.test.js's
  // KNOWN_GAPS entry for 'monster-plaguebearer:global:0'.
  battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});
register('monster-hungry-dead', {
  aftermath: [{
    // "Destroy 1 card for each disease present (remember, monster effects
    // do not affect disease cards). Gain 1 disease."
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const diseaseCount = player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Disease')).length;
      let nextState = state;
      for (let i = 0; i < diseaseCount; i += 1) {
        const current = nextState.players.find((p) => p.id === playerId);
        const targetId = current.hand.find((id) => !getInstanceDefinition(nextState, id).traits.includes('Disease'));
        if (!targetId) break;
        nextState = destroyFromHand(nextState, playerId, targetId, { battleEffect: true });
      }
      return gainCurse(nextState, playerId);
    },
  }],
});
register('monster-plague-zombie', { battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }] });
register('monster-restless-corpse', { battle: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }] });

// --- Abyssal Horde (deferred - see buildCardData.mjs's HORDE_MONSTER_GROUPS;
// excluded from the selectable dungeon setup pool, but still card-defined,
// so its ability text still needs coverage) ---

register('monster-doomspawn', {
  // "specialEffects: Add Health +1 for each other Doomspawn in the hall."
  // Same "for each other same-group card in the hall" shape as ToR's own
  // Drakeclan Laird (effectRegistry/monsters.js, kobold chain-kill group) -
  // computed from printed/base Health for every instance involved (never
  // getInstanceDefinition's possibly-already-boosted value), same
  // compounding-bug precaution as that card's own comment explains.
  battle: [{
    resolve: (state, { instanceId, monsterInstanceId }) => {
      const selfBaseDef = getDefinition(state.instances[monsterInstanceId].definitionId);
      const otherDoomspawn = state.dungeonHall.filter((id) => id && id !== monsterInstanceId
        && getDefinition(state.instances[id].definitionId).id === 'monster-doomspawn');
      return applyInstanceOverride(state, instanceId, { health: (selfBaseDef.health || 0) + otherDoomspawn.length });
    },
  }],
  aftermath: [{
    // "Gain 1 disease for each Abyssal, Doomknight, and Undead monster
    // present (in your hand)." Monster-type cards essentially never sit in
    // hand under normal play - correct as written even though it will
    // rarely trigger.
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const count = player.hand.filter((id) => {
        const def = getInstanceDefinition(state, id);
        if (def.type !== 'monster') return false;
        return def.traits.includes('Abyssal') || def.traits.includes('Doomknight') || def.traits.includes('Undead');
      }).length;
      let nextState = state;
      for (let i = 0; i < count; i += 1) nextState = gainCurse(nextState, playerId);
      return nextState;
    },
  }],
});

// ==========================================================================
// Worlds Collide
// ==========================================================================
// No rulebook available (same caveat as Numenera/Start/ItA) - extra caution
// on anything ambiguous, ties/tie-breaks default to "no printed tie-break
// text -> auto-pick the first match found" (Eruptor's own ToR precedent),
// not a player choice, unless the specific WC card's own text calls for one.

// Sibling of revealTopFiveDestroyMatching above, but destroys EVERY matching
// card among the 5 revealed (not just 1) - Razorback/Regicide's own
// "Destroy all revealed [class]" text, plural and uncapped, unlike Elemental
// Pain's "destroys 1 revealed card matching" shape.
function revealTopFiveDestroyAllMatching(matches) {
  return (state) => {
    let nextState = state;
    for (const p of state.players) {
      const revealed = [];
      for (let i = 0; i < 5; i += 1) {
        const { state: afterReveal, instanceId } = revealTopOfOwnDeck(nextState, p.id);
        nextState = afterReveal;
        if (instanceId) revealed.push(instanceId);
      }
      const targetIds = revealed.filter((id) => matches(getInstanceDefinition(nextState, id)));
      for (const targetId of targetIds) {
        nextState = { ...nextState, players: nextState.players.map((pp) => (pp.id === p.id ? { ...pp, discard: pp.discard.filter((id) => id !== targetId) } : pp)) };
      }
    }
    return nextState;
  };
}

register('monster-razorback', {
  aftermath: [{ resolve: destroyByTrait('Razorback: destroy all rangers.', (def) => def.traits.includes('Ranger')) }],
  breach: [{ resolve: revealTopFiveDestroyAllMatching((def) => def.traits.includes('Ranger')) }],
});
register('monster-the-bloodless', {
  // "Aftermath: Destroy the hero with the highest Attack Value (including
  // weapon bonuses)." estimateHeroAttackValue already folds in the equipped
  // weapon's own contribution - "including weapon bonuses" is just making
  // that explicit, not asking for anything extra.
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroes = heroesPresent(state, playerId).map((id) => ({ id, value: estimateHeroAttackValue(state, player, id) }));
      if (!heroes.length) return state;
      const max = Math.max(...heroes.map((h) => h.value));
      const target = heroes.find((h) => h.value === max);
      return destroyFromHand(state, playerId, target.id, { battleEffect: true });
    },
  }],
  // "Breach: All players reveal the top 5 cards from their decks. Destroy
  // all revealed wizards. Discard the rest." Missing entirely from source
  // data until the WC numeric-stat audit (2026-07-22) found it on the card
  // scan - same revealTopFiveDestroyAllMatching shape as this group's own
  // Razorback (rangers) and Regicide (clerics).
  breach: [{ resolve: revealTopFiveDestroyAllMatching((def) => def.traits.includes('Wizard')) }],
});
register('monster-regicide', {
  // "specialEffects: Immune to Magic Attack." - combat/attackMath.js MONSTER_IMMUNITIES.
  breach: [{ resolve: revealTopFiveDestroyAllMatching((def) => def.traits.includes('Cleric')) }],
});
register('monster-tendril-minion', {
  // "Aftermath: Gain 1 disease. Gain 1 additional disease if a disease is
  // present." "Present" read as in-hand, this engine's standing convention.
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const hasDisease = player.hand.some((id) => getInstanceDefinition(state, id).traits.includes('Curse'));
      let nextState = gainCurse(state, playerId);
      if (hasDisease) nextState = gainCurse(nextState, playerId);
      return nextState;
    },
  }],
});
register('monster-usurper', {
  // "Aftermath: Destroy all but 2 heroes." Queues one destroy choice per
  // hero beyond the first 2 - same repeated-queueHandChoice-loop shape as
  // village-phase-disruptor's "destroy 2 other cards" (dropDeadEntries
  // self-heals if fewer heroes remain than expected by the time later
  // entries in the loop would resolve).
  aftermath: [{
    resolve: (state, { playerId }) => {
      const excess = heroesPresent(state, playerId).length - 2;
      let nextState = state;
      for (let i = 0; i < excess; i += 1) {
        const candidateInstanceIds = heroesPresent(nextState, playerId);
        nextState = queueHandChoice(nextState, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Usurper: destroy all but 2 heroes.' });
      }
      return nextState;
    },
  }],
  // "Spoils: Buy 1 village card." Untranscribed (along with the card's
  // Immune to Magic Attack line) until Matt's bug report ("The usurper
  // enemy's spoils ability was not triggered") sent triage back to the
  // card scan, which prints both. Plain Bounty Hunter "Buy 1 village
  // card" shape.
  spoils: [buyOneCard({
    errorMessage: 'No village cards remain to buy.',
    includeBasics: false,
  })],
});

register('monster-lord-mortis', {
  // "specialEffects: Suffer Light -1." - combat/resolveCombat.js SUFFER_LIGHT_PENALTY.
});
register('monster-the-prince', {
  battle: [{ resolve: (state, { playerId }) => heroesSufferStrength(state, playerId, 2) }],
  aftermath: [{ resolve: destroyByTrait('The Prince: destroy 1 fighter.', (def) => def.traits.includes('Fighter')) }],
});
register('monster-judgement', {
  // "Battle: Each hero suffers Physical Attack -1 and Strength -2."
  battle: [{
    resolve: (state, { playerId }) => heroesSufferStrength(heroesSufferPhysicalAttack(state, playerId, 1), playerId, 2),
  }],
});
register('monster-knightmare', {
  aftermath: [{ resolve: destroyByTrait('Knightmare: destroy 1 fighter.', (def) => def.traits.includes('Fighter')) }],
});
register('monster-darkness', {
  // "specialEffects: Ignore unequipped heroes." - MONSTER_IMMUNITIES
  // requiresEquippedHero. "Suffer Light -1." - SUFFER_LIGHT_PENALTY.
});

register('monster-ebon-fume', {
  // "specialEffects: Immune to Magic Attack." - MONSTER_IMMUNITIES.
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = highestStrengthHeroCandidates(state, player);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Ebon Fume: destroy 1 hero with the highest Strength.' });
    },
  }],
  trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 3) }],
});
register('monster-skaladak', {
  aftermath: [{ resolve: destroyByTrait('Skaladak: destroy 1 weapon.', (def) => def.traits.includes('Weapon')) }],
});
register('monster-mythlurian', {
  aftermath: [{ resolve: destroyByTrait('Mythlurian: destroy 1 hero.', (def) => isHeroLike(def)) }],
});
register('monster-tyxr-the-old', {
  aftermath: [{ resolve: destroyByTrait('Tyxr the Old: destroy 1 hero.', (def) => isHeroLike(def)) }],
  breach: [{
    resolve: (state) => {
      let nextState = state;
      for (const p of state.players) {
        for (let i = 0; i < 2; i += 1) {
          const candidateInstanceIds = nextState.players.find((pp) => pp.id === p.id).hand;
          nextState = queueHandChoice(nextState, { playerId: p.id, verb: 'discard', candidateInstanceIds, reason: 'Tyxr the Old: each player discards 2 cards.' });
        }
      }
      return nextState;
    },
  }],
  trophy: [{ resolve: (state, { playerId }) => grantMagicAttack(state, playerId, 2) }],
});
register('monster-uyril-unending', {
  // "specialEffects: Halve Total Attack Value (round down) unless Magic
  // Attack is present." - combat/resolveCombat.js HALVE_UNLESS.
  aftermath: [{ resolve: destroyByTrait('Uyril Unending: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
  trophy: [{ resolve: (state, { playerId }) => grantPhysicalAttack(state, playerId, 1) }],
});

// Skywyrm Overlord: "specialEffects: Ignore 1 hero for each class present."
// A specialEffects/alwaysActive line (not Battle-timed ability text), so it
// can't go through the normal registry dispatch - special-cased directly in
// combat/resolveCombat.js instead, alongside Royal Guard's own inline
// specialEffects block. Its own Aftermath ("Destroy each hero that was
// ignored") reads back turnFlags.ignoredInstanceIds here.
// This card was mistitled "Skywyrm Scavenger" in source data (a WC audit,
// 2026-07-22, fix) - it's actually a separate, distinct card from the
// (differently-spelled, lower-Health) "Skywyrm Scavanger" registered just
// below, whose own title also had a typo ("Scavanger") fixed in the same
// pass. See docs/expansion-audit.md for the full writeup.
register('monster-skywyrm-overlord', {
  aftermath: [
    {
      resolve: (state, { playerId }) => {
        const player = state.players.find((p) => p.id === playerId);
        const toDestroy = player.hand.filter((id) => player.turnFlags.ignoredInstanceIds?.includes(id));
        let nextState = state;
        for (const id of toDestroy) {
          const current = nextState.players.find((p) => p.id === playerId);
          if (!current.hand.includes(id)) continue;
          nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
        }
        return nextState;
      },
    },
    {
      // "If not defeated, place this card in the dungeon deck as the third
      // card down." dungeonDeck's own index 0 = top (dungeon/hall.js's
      // compactAndRefill draws via .shift()) - "third card down" = index 2
      // at the moment of placement. Note this is BEFORE the hall-refill
      // draw that immediately follows (its own now-empty rank needs
      // refilling from the same deck) - that draw removes whatever is
      // currently on top, so the card's FINAL resting position ends up one
      // slot higher (index 1) once the turn's dust settles. Treated as the
      // literal, atomic instruction rather than pre-compensating for a
      // downstream refill that isn't guaranteed to always consume exactly
      // 1 card (e.g. if some other effect had already opened a second hall
      // slot this same turn) - see docs/expansion-audit.md.
      // Also clears its own hall slot here (instead of leaving that to
      // resolveCombat.js's normal retreat-to-bottom-of-deck branch) - that
      // branch reads `stillAtRank` to decide whether to ALSO move this same
      // instanceId to the bottom of dungeonDeck, which would leave a
      // duplicate reference to it in the deck if both ran. Clearing the
      // slot here makes `stillAtRank` false, so the normal branch correctly
      // treats it as "already moved by its own effect" (the same
      // resolveCombat.js path Sathosh's own hall relocation relies on) and
      // skips its own retreat handling.
      resolve: (state, { won, monsterInstanceId, hallRankIndex }) => {
        if (won) return state;
        const dungeonHall = [...state.dungeonHall];
        if (dungeonHall[hallRankIndex] === monsterInstanceId) dungeonHall[hallRankIndex] = null;
        const dungeonDeck = [...state.dungeonDeck];
        dungeonDeck.splice(2, 0, monsterInstanceId);
        return { ...state, dungeonHall, dungeonDeck };
      },
    },
  ],
});
register('monster-skywyrm-scavenger', {
  // "specialEffects: Ignore thieves and rangers." combat/attackMath.js
  // MONSTER_IMMUNITIES's heroClasses (plural) field - a flat class-based
  // void (every Thief/Ranger present contributes nothing, no per-class pick
  // needed), unlike Skywyrm Overlord's own "1 per class present" shape
  // above. Title typo ("Scavanger") fixed at the source, WC audit
  // 2026-07-22 - id changed to match (was 'monster-skywyrm-scavanger'
  // pointed to a DIFFERENT card before this fix; see
  // docs/expansion-audit.md).
  aftermath: [{ resolve: destroyByTrait('Skywyrm Scavenger: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
});
register('monster-skywyrm-mage', {
  aftermath: [{ resolve: destroyByTrait('Skywyrm Mage: destroy 1 fighter.', (def) => def.traits.includes('Fighter')) }],
});
register('monster-skywyrm-warrior', {
  // "specialEffects: Ignore fighters."
  aftermath: [{ resolve: destroyByTrait('Skywyrm Warrior: destroy 1 wizard.', (def) => def.traits.includes('Wizard')) }],
});
register('monster-skywyrm-priest', {
  // "specialEffects: Ignore clerics."
  aftermath: [{ resolve: destroyByTrait('Skywyrm Priest: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
});

// Worlds Collide, Frost/Stone Giant: "Destroy N heroes with Strength <= X."
// Queues one destroy choice per hero beyond the cap (dropDeadEntries
// self-heals if fewer heroes qualify than N by the time later entries would
// resolve - "Do What You Can").
function destroyHeroesAtOrBelowStrength(reason, count, maxStrength) {
  return (state, { playerId }) => {
    let nextState = state;
    for (let i = 0; i < count; i += 1) {
      const candidateInstanceIds = heroesPresent(nextState, playerId)
        .filter((id) => (getInstanceDefinition(nextState, id).strength || 0) <= maxStrength);
      nextState = queueHandChoice(nextState, { playerId, verb: 'destroy', candidateInstanceIds, reason });
    }
    return nextState;
  };
}
// "Destroy 1 hero/weapon with the lowest Strength/Weight." No tie-break
// text printed. Matt (bug report, Fire Giant): "It didn't let me choose
// which of the lowest strength heroes to destroy. Whenever that is more
// than one possible target, I have to be allowed to pick." Queues a
// choice among every candidate tied at the lowest stat value (same fix
// as monster-eruptor above, which prints near-identical text but isn't
// wired through this shared helper).
function destroyLowestStat(reason, matches, statKey) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidates = player.hand.filter((id) => matches(getInstanceDefinition(state, id)));
    if (!candidates.length) return state;
    const min = Math.min(...candidates.map((id) => getInstanceDefinition(state, id)[statKey] || 0));
    const candidateInstanceIds = candidates.filter((id) => (getInstanceDefinition(state, id)[statKey] || 0) === min);
    return queueHandChoice(state, {
      playerId, verb: 'destroy', candidateInstanceIds, reason,
    });
  };
}

// Worlds Collide, Titan: "Destroy all heroes except the 2 with the highest
// Strength. Destroy all weapons except 1 with the highest Weight." A "keep
// only the top N by stat, destroy the rest" shape distinct from every helper
// above (those destroy the LOWEST/a fixed count, never "everything but the
// top N"). Anything strictly below the Nth-highest value is an unconditional
// destroy (the printed text leaves no ambiguity there); a tie for the exact
// cutoff value IS a real choice (which of the tied cards survive), handled
// the same way destroyLowestStat already treats ties - queued one at a time
// among just the tied group.
function destroyAllExceptHighestStat(reason, matches, statKey, keepCount) {
  return (state, { playerId }) => {
    const player = state.players.find((p) => p.id === playerId);
    const candidates = player.hand.filter((id) => matches(getInstanceDefinition(state, id)));
    if (candidates.length <= keepCount) return state;
    const statOf = (id) => getInstanceDefinition(state, id)[statKey] || 0;
    const sorted = [...candidates].sort((a, b) => statOf(b) - statOf(a));
    const cutoffValue = statOf(sorted[keepCount - 1]);
    const aboveCutoffCount = sorted.filter((id) => statOf(id) > cutoffValue).length;
    const atCutoff = sorted.filter((id) => statOf(id) === cutoffValue);
    const belowCutoff = sorted.filter((id) => statOf(id) < cutoffValue);
    let nextState = state;
    for (const id of belowCutoff) {
      nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
    }
    const keepFromTied = Math.max(0, keepCount - aboveCutoffCount);
    const destroyFromTiedCount = atCutoff.length - keepFromTied;
    for (let i = 0; i < destroyFromTiedCount; i += 1) {
      nextState = queueHandChoice(nextState, {
        playerId, verb: 'destroy', candidateInstanceIds: atCutoff, reason,
      });
    }
    return nextState;
  };
}

register('monster-frost-giant', {
  // "Destroy 2 heroes with Strength 5 or lower. Destroy 1 weapon with the
  // lowest Weight." - both clauses are a single printed Aftermath line.
  aftermath: [{
    resolve: (state, ctx) => {
      let nextState = destroyHeroesAtOrBelowStrength('Frost Giant: destroy 2 heroes with Strength 5 or lower.', 2, 5)(state, ctx);
      nextState = destroyLowestStat('Frost Giant: destroy 1 weapon with the lowest Weight.', (def) => def.traits.includes('Weapon'), 'weight')(nextState, ctx);
      return nextState;
    },
  }],
});
register('monster-stone-giant', {
  aftermath: [{ resolve: destroyHeroesAtOrBelowStrength('Stone Giant: destroy 2 heroes with Strength 4 or lower.', 2, 4) }],
});
register('monster-titan', {
  // "Destroy all heroes except the 2 with the highest Strength. Destroy all
  // weapons except 1 with the highest Weight." Missing entirely from source
  // data until the WC numeric-stat audit (2026-07-22) found it on the card
  // scan (this monster's entire Aftermath had gone untranscribed).
  aftermath: [{
    resolve: (state, ctx) => {
      let nextState = destroyAllExceptHighestStat('Titan: destroy all heroes except the 2 with the highest Strength.', (def) => isHeroLike(def), 'strength', 2)(state, ctx);
      nextState = destroyAllExceptHighestStat('Titan: destroy all weapons except 1 with the highest Weight.', (def) => def.traits.includes('Weapon'), 'weight', 1)(nextState, ctx);
      return nextState;
    },
  }],
});
register('monster-mountain-giant', {
  aftermath: [{ resolve: destroyLowestStat('Mountain Giant: destroy 1 hero with the lowest Strength.', (def) => isHeroLike(def), 'strength') }],
});
register('monster-fire-giant', {
  aftermath: [{ resolve: destroyLowestStat('Fire Giant: destroy 1 hero with the lowest Strength.', (def) => isHeroLike(def), 'strength') }],
  // "Trophy: Add Strength +3 to one hero." Missing entirely from source data
  // until the WC numeric-stat audit (2026-07-22) found it on the card scan.
  // No tie-break text - defaults to the first hero present, same convention
  // as Gnoll Ravager's own Trophy above.
  trophy: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroId = player.hand.find((id) => isHeroLike(getInstanceDefinition(state, id)));
      if (!heroId) return state;
      return grantTemporaryStrength(state, playerId, heroId, 3);
    },
  }],
});

register('monster-black-slime', {
  aftermath: [{ resolve: destroyByTrait('Black Slime: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
});
register('monster-green-blob', {
  aftermath: [{ resolve: destroyByTrait('Green Blob: destroy 1 food.', (def) => def.traits.includes('Food')) }],
});
register('monster-red-jelly', {
  aftermath: [{ resolve: destroyByTrait('Red Jelly: destroy 1 weapon.', (def) => def.traits.includes('Weapon')) }],
});
register('monster-gray-ooze', {
  // "Aftermath: Destroy 1 hero unless at least 1 weapon is equipped."
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      if (Object.keys(player.equippedWeapons).length > 0) return state;
      const candidateInstanceIds = heroesPresent(state, playerId);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Gray Ooze: destroy 1 hero unless at least 1 weapon is equipped.' });
    },
  }],
  // "Spoils: Buy 1 food." Used to auto-pick the cheapest Food stack; now
  // offers the choice like every other "Buy 1 X" (cards/buyOneCard.js).
  spoils: [buyOneCard({
    matches: (def) => def.traits.includes('Food'),
    onEmpty: 'skip',
  })],
});
register('monster-noxious-slag', {
  // "specialEffects: Immune to edged weapons." - MONSTER_IMMUNITIES.
  // "Halve Magic Attack (round down)." - combat/resolveCombat.js.
});

// Worlds Collide Spider group: "Each player destroys N hero(es) or shows a
// hand with fewer." - "shows a hand with none/fewer" is flavor text for
// physical play (proving you can't fully comply); digitally, dropDeadEntries
// already satisfies "Do What You Can" by auto-dropping a queued choice once
// no valid candidates remain, so no separate "prove it" step is needed.
function eachPlayerDestroysHeroes(reason, count) {
  return (state) => {
    let nextState = state;
    for (const p of state.players) {
      for (let i = 0; i < count; i += 1) {
        const candidateInstanceIds = heroesPresent(nextState, p.id);
        nextState = queueHandChoice(nextState, { playerId: p.id, verb: 'destroy', candidateInstanceIds, reason });
      }
    }
    return nextState;
  };
}

register('monster-spider-queen', {
  battle: [{
    resolve: (state, { playerId }) => {
      let nextState = state;
      for (let i = 0; i < 2; i += 1) {
        const candidateInstanceIds = battleTargetableHeroes(nextState, nextState.players.find((p) => p.id === playerId));
        nextState = queueHandChoice(nextState, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Spider Queen: destroy 2 heroes.' });
      }
      return nextState;
    },
  }],
});
register('monster-firefang', {
  breach: [{ resolve: eachPlayerDestroysHeroes('Firefang: each player destroys 1 hero or shows a hand with none.', 1) }],
});
register('monster-dread', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Dread: destroy 1 hero.' });
    },
  }],
  breach: [{
    // "The active player destroys all heroes present." Only the active
    // player (not "each player" - contrast Firefang/Green Spinner/Widowmaker
    // just above/below), and unconditionally all of them, not a choice.
    resolve: (state, { playerId }) => {
      let nextState = state;
      for (const id of heroesPresent(state, playerId)) {
        const current = nextState.players.find((p) => p.id === playerId);
        if (!current.hand.includes(id)) continue;
        nextState = destroyFromHand(nextState, playerId, id);
      }
      return nextState;
    },
  }],
});
register('monster-green-spinner', {
  breach: [{ resolve: eachPlayerDestroysHeroes('Green Spinner: each player destroys 2 heroes or shows a hand with fewer.', 2) }],
});
register('monster-widowmaker', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'ignore', candidateInstanceIds, reason: 'Widowmaker: ignore 1 hero.' });
    },
  }],
  breach: [{ resolve: eachPlayerDestroysHeroes('Widowmaker: each player destroys 1 hero or shows a hand with none.', 1) }],
});

register('monster-lord-of-death', {
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(gainCurse(state, playerId), playerId) }],
});
register('monster-kingdom', {
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});
register('monster-famine', {
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});
register('monster-harbinger', {
  aftermath: [{ resolve: destroyByTrait('Harbinger: destroy 1 spell.', (def) => def.traits.includes('Spell')) }],
});
register('monster-suffering', {
  battle: [{ resolve: (state, { playerId }) => heroesSufferStrength(state, playerId, 2) }],
  // "Aftermath: Gain 1 disease." Missing entirely from source data until the
  // WC numeric-stat audit (2026-07-22) found it on the card scan - same
  // gainCurse shape as this group's own Kingdom/Famine above.
  aftermath: [{ resolve: (state, { playerId }) => gainCurse(state, playerId) }],
});

// Undead Spectral group: "specialEffects: Immune to Physical Attack and
// Magic Attack unless you [pay a cost] when you select this monster to
// fight." All 5 cards special-cased in combat/attackMath.js
// (UNDEAD_SPECTRAL_IMMUNITY) + combat/resolveCombat.js (the per-monster
// cost payment map, opted into via choices.payUndeadSpectralCost on
// CHOOSE_MONSTER) - no registry handler needed here.
register('monster-souleater', {}); // "...unless you discard 3 cards"
register('monster-cruel-apparition', {}); // "...unless you destroy 1 hero"
register('monster-will-o-the-wisp', {}); // "...unless you destroy 1 food"
register('monster-wight', {}); // "...unless you discard 2 XP"
register('monster-devourer', {}); // "...unless you destroy 1 monster"

register('monster-haunt', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'ignore', candidateInstanceIds, reason: 'Haunt: ignore 1 hero.' });
    },
  }],
});
register('monster-ghost', {
  // "specialEffects: Halve Physical Attack (round down) without Magic
  // Attack present." - combat/resolveCombat.js.
  battle: [{ resolve: (state, { playerId }) => heroesSufferStrength(state, playerId, 2) }],
});
register('monster-wraith', {
  aftermath: [{ resolve: destroyByTrait('Wraith: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
});
register('monster-revenant', {
  // "specialEffects: Magic Attack required." - MONSTER_IMMUNITIES.
  // Both lines below were missing entirely from source data until the WC
  // numeric-stat audit (2026-07-22) found them on the card scan.
  battle: [{ resolve: (state, { playerId }) => heroesSufferStrength(state, playerId, 4) }],
  // Same "destroy each hero with Strength 0" shape as Lich Lord's own
  // Aftermath elsewhere in this file.
  aftermath: [{ resolve: destroyByTrait('Revenant: destroy each hero with Strength 0.', (def) => isHeroLike(def) && (def.strength || 0) === 0) }],
});
register('monster-spectre', {
  aftermath: [{ resolve: destroyByTrait('Spectre: destroy 1 Regular.', (def) => def.id === 'basic-regular') }],
});

// --- Guardian (deferred entirely - see buildCardData.mjs's
// EXCLUDED_MONSTER_GROUPS; not even card-defined, so no coverage needed) ---

